const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api';

/**
 * Effectue une requête authentifiée auprès de l'API Symfony.
 * Les credentials (cookies de session) sont transmis pour le mode session.
 */
async function apiFetch(path: string, options: RequestInit = {}) {
  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    const echec = new Error(error.message || `Erreur ${response.status}`) as Error & {
      statut?: number;
      donnees?: any;
    };
    // Le corps de la réponse suit l'erreur : la connexion en deux étapes s'y
    // annonce par `besoin2fa`, ce que le message seul ne dirait pas.
    echec.statut = response.status;
    echec.donnees = error;
    throw echec;
  }

  return response.json();
}

export async function fetchMe() {
  return apiFetch('/me');
}

/**
 * Connexion. Le code de la seconde étape n'accompagne la requête que si le
 * compte est protégé — le serveur le réclame alors par `besoin2fa`.
 */
export async function login(
  email: string,
  password: string,
  code?: string,
  fiabiliserAppareil?: boolean,
  /**
   * La signature de la cle, quand le serveur l a reclamee.
   *
   * Elle voyage dans le MEME appel que le mot de passe, comme le code a six
   * chiffres : le pare-feu revalide le mot de passe a chaque tentative, et une
   * seconde adresse pour le second facteur voudrait dire une session ouverte
   * entre les deux — c est-a-dire exactement ce qu on detruit en refusant.
   */
  cle?: Record<string, string>,
) {
  return apiFetch('/login', {
    method: 'POST',
    body: JSON.stringify({ email, password, code, fiabiliserAppareil, cle }),
  });
}

/* --------------------------- Les cles de securite ------------------------- */

export interface CleSecurite {
  id: number;
  nom: string;
  creeLe: string;
  dernierUsageLe: string | null;
}

/** Mes cles, la plus recemment employee d abord. */
export async function fetchMesCles(): Promise<{ success: boolean; cles: CleSecurite[] }> {
  return apiFetch('/me/cles');
}

/** Le defi d enrolement : a passer tel quel a creerCle(). */
export async function defiEnrolementCle() {
  return apiFetch('/me/cles/defi', { method: 'POST' });
}

/** Enregistre la cle que le navigateur vient de creer. */
export async function enrolerCle(charge: Record<string, string> & { nom?: string }) {
  return apiFetch('/me/cles', { method: 'POST', body: JSON.stringify(charge) });
}

export async function renommerCle(id: number, nom: string) {
  return apiFetch(`/me/cles/${id}`, { method: 'PATCH', body: JSON.stringify({ nom }) });
}

export async function retirerMaCle(id: number) {
  return apiFetch(`/me/cles/${id}`, { method: 'DELETE' });
}

/** Les cles d un membre, pour le president et l editeur. */
export async function fetchClesDunMembre(membre: number): Promise<{ success: boolean; cles: CleSecurite[] }> {
  return apiFetch(`/admin/membres/${membre}/cles`);
}

/**
 * Le president ou l editeur retire la cle d un autre.
 *
 * C est la porte de secours du vendredi soir : sans elle, un tresorier qui
 * egare son trousseau n a plus acces a la caisse, et la seule issue serait une
 * intervention en base — que personne ne sait faire un dimanche.
 */
export async function retirerCleDunMembre(membre: number, cle: number) {
  return apiFetch(`/admin/membres/${membre}/cles/${cle}`, { method: 'DELETE' });
}

/** L'état de la connexion en deux étapes du membre connecté. */
export async function fetchEtat2fa() {
  return apiFetch('/me/2fa');
}

/** Prépare l'enrôlement : rend le QR et la transcription de la graine. */
export async function preparer2fa(motDePasse: string) {
  return apiFetch('/me/2fa/preparer', {
    method: 'POST',
    body: JSON.stringify({ motDePasse }),
  });
}

/** Confirme l'enrôlement avec un premier code ; rend les codes de secours. */
export async function activer2fa(code: string) {
  return apiFetch('/me/2fa/activer', { method: 'POST', body: JSON.stringify({ code }) });
}

export async function desactiver2fa(motDePasse: string, code: string) {
  return apiFetch('/me/2fa/desactiver', {
    method: 'POST',
    body: JSON.stringify({ motDePasse, code }),
  });
}

export async function regenererCodesSecours2fa(motDePasse: string) {
  return apiFetch('/me/2fa/codes-secours', {
    method: 'POST',
    body: JSON.stringify({ motDePasse }),
  });
}

/** Dépannage : un administrateur retire la protection d'un membre bloqué. */
export async function retirer2faMembre(id: number, motDePasse: string) {
  return apiFetch(`/admin/membres/${id}/2fa`, {
    method: 'DELETE',
    body: JSON.stringify({ motDePasse }),
  });
}

export async function logout() {
  return apiFetch('/logout', { method: 'POST' });
}

/** Vitrine publique des sponsors : visuels valides, coordonnees et reseaux. */
export async function fetchVitrineSponsors() {
  return apiFetch('/sponsors/public/vitrine');
}

export async function fetchSponsorPlans() {
  return apiFetch('/sponsors/plans');
}

export async function soumettreSouscription(payload: Record<string, any>) {
  return apiFetch('/sponsors/public/soumettre', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function fetchEvenementsPublics() {
  const data = await apiFetch('/evenements/public');
  // Le backend retourne { success: true, evenements: [...] }
  return Array.isArray(data) ? data : data.evenements ?? [];
}

export async function fetchPaiementInfos(token: string) {
  return apiFetch(`/paiement/infos/${token}`);
}

export async function choisirModePaiement(token: string, mode: 'carte' | 'virement' | 'cheque') {
  return apiFetch(`/paiement/choisir/${token}`, {
    method: 'POST',
    body: JSON.stringify({ mode }),
  });
}

export async function createStripeSession(token: string) {
  return apiFetch(`/paiement/stripe/session/${token}`, {
    method: 'POST',
  });
}

export async function fetchFactureStatus(token: string) {
  return apiFetch(`/paiement/facture/${token}`);
}

export async function fetchParametresAdmin() {
  return apiFetch('/admin/parametres');
}

export async function updateParametreAdmin(cle: string, valeur: string | null) {
  return apiFetch(`/admin/parametres/${cle}`, {
    method: 'PUT',
    body: JSON.stringify({ valeur }),
  });
}

/** Remplace la liste des vêtements proposés aux bénévoles et leurs tailles. */
export async function updateVetementsAdmin(
  vetements: { code?: string; label: string; tailles: string[] }[]
) {
  return apiFetch('/admin/referentiels/vetements', {
    method: 'PUT',
    body: JSON.stringify({ vetements }),
  });
}

/** Vide le coffre-fort : tous les comptes, contre le mot de passe de l'admin. */
export async function viderVaultAdmin(motDePasse: string) {
  return apiFetch('/bureau/vault/vider', {
    method: 'POST',
    body: JSON.stringify({ motDePasse }),
  });
}

/* ----------------------------- Adhérents ----------------------------- */

/** Le registre : recherche dès deux caractères, filtres et configuration. */
export async function fetchAdherents(params: { q?: string; discipline?: string; inactifs?: boolean } = {}) {
  const requete = new URLSearchParams();
  if (params.q) requete.set('q', params.q);
  if (params.discipline) requete.set('discipline', params.discipline);
  if (params.inactifs) requete.set('inactifs', '1');
  const chaine = requete.toString();
  return apiFetch(`/bureau/adherents${chaine ? `?${chaine}` : ''}`);
}

export async function fetchAdherent(id: number) {
  return apiFetch(`/bureau/adherents/${id}`);
}

export async function createAdherent(donnees: Record<string, unknown>) {
  return apiFetch('/bureau/adherents', { method: 'POST', body: JSON.stringify(donnees) });
}

export async function updateAdherent(id: number, donnees: Record<string, unknown>) {
  return apiFetch(`/bureau/adherents/${id}`, { method: 'PUT', body: JSON.stringify(donnees) });
}

export async function deleteAdherent(id: number, definitif = false) {
  return apiFetch(`/bureau/adherents/${id}${definitif ? '?definitif=1' : ''}`, { method: 'DELETE' });
}

/** Les contacts d'urgence : envoyés en bloc, la liste est courte. */
export async function updateContactsAdherent(id: number, contacts: unknown[]) {
  return apiFetch(`/bureau/adherents/${id}/contacts`, { method: 'PUT', body: JSON.stringify({ contacts }) });
}

export async function deposerDocumentAdherent(id: number, formulaire: FormData) {
  const reponse = await fetch(`${API_BASE}/bureau/adherents/${id}/documents`, {
    method: 'POST',
    credentials: 'include',
    body: formulaire,
  });
  const donnees = await reponse.json().catch(() => ({}));
  if (!reponse.ok) throw new Error(donnees.message || `Erreur ${reponse.status}`);
  return donnees;
}

export async function supprimerDocumentAdherent(document: number) {
  return apiFetch(`/bureau/adherents/documents/${document}`, { method: 'DELETE' });
}

/* ------------------------ Licences et cotisations ---------------------- */

export async function fetchSaisons() {
  return apiFetch('/bureau/saisons');
}

export async function createSaison(donnees: Record<string, unknown>) {
  return apiFetch('/bureau/saisons', { method: 'POST', body: JSON.stringify(donnees) });
}

export async function updateSaison(id: number, donnees: Record<string, unknown>) {
  return apiFetch(`/bureau/saisons/${id}`, { method: 'PUT', body: JSON.stringify(donnees) });
}

export async function deleteSaison(id: number) {
  return apiFetch(`/bureau/saisons/${id}`, { method: 'DELETE' });
}

/** Les licences d'une saison, avec l'état de leur règlement. */
export async function fetchLicences(params: { saison?: number | string; statut?: string; paiement?: string } = {}) {
  const requete = new URLSearchParams();
  if (params.saison) requete.set('saison', String(params.saison));
  if (params.statut) requete.set('statut', params.statut);
  if (params.paiement) requete.set('paiement', params.paiement);
  const chaine = requete.toString();
  return apiFetch(`/bureau/licences${chaine ? `?${chaine}` : ''}`);
}

export async function createLicence(adherent: number, donnees: Record<string, unknown>) {
  return apiFetch(`/bureau/adherents/${adherent}/licences`, { method: 'POST', body: JSON.stringify(donnees) });
}

export async function updateLicence(id: number, donnees: Record<string, unknown>) {
  return apiFetch(`/bureau/licences/${id}`, { method: 'PUT', body: JSON.stringify(donnees) });
}

/** Le versement encaissé par le bureau : espèces, chèque, virement, coupon. */
export async function encaisserCotisation(licence: number, donnees: Record<string, unknown>) {
  return apiFetch(`/bureau/licences/${licence}/reglements`, { method: 'POST', body: JSON.stringify(donnees) });
}

/**
 * Annule un versement — sans l'effacer.
 *
 * Il n'existe plus de route qui détruise un règlement, pour aucun rôle : un
 * logiciel qui tient des recettes doit garantir l'inaltérabilité de ce qui y a
 * été saisi (NF525). La ligne reste, sort des totaux, et s'affiche barrée avec
 * son motif.
 *
 * Le motif est obligatoire côté serveur : « erreur de montant » et « chèque
 * impayé » ne se rattrapent pas de la même façon.
 */
export async function annulerReglement(id: number, motif: string) {
  return apiFetch(`/bureau/reglements/${id}/annuler`, {
    method: 'POST',
    body: JSON.stringify({ motif }),
  });
}

/** L'attestation de licence et de paiement, en PDF. */
export function urlAttestation(licence: number): string {
  return `${API_BASE}/bureau/licences/${licence}/attestation`;
}

/* ----------------------------- Équipements ----------------------------- */

/** L'inventaire : ce qui est sorti, ce qui est attendu, ce qui traîne. */
export async function fetchEquipements(params: { statut?: string; type?: string; etat?: string; saison?: number | string } = {}) {
  const requete = new URLSearchParams();
  Object.entries(params).forEach(([cle, valeur]) => {
    if (valeur) requete.set(cle, String(valeur));
  });
  const chaine = requete.toString();
  return apiFetch(`/bureau/equipements${chaine ? `?${chaine}` : ''}`);
}

export async function remettreEquipement(adherent: number, donnees: Record<string, unknown>) {
  return apiFetch(`/bureau/adherents/${adherent}/equipements`, { method: 'POST', body: JSON.stringify(donnees) });
}

/** Le retour d'un prêt, avec l'état constaté. */
export async function rendreEquipement(id: number, donnees: Record<string, unknown>) {
  return apiFetch(`/bureau/equipements/${id}/retour`, { method: 'POST', body: JSON.stringify(donnees) });
}

export async function supprimerEquipement(id: number) {
  return apiFetch(`/bureau/equipements/${id}`, { method: 'DELETE' });
}

/* -------------------- Règlement en ligne, sans compte ------------------- */

/**
 * Ce que la commission bancaire ajoute au montant demandé.
 *
 * Rendue même quand le club ne reporte rien — `frais` vaut alors zéro et
 * `demande` égale `voulu`. L'écran n'a ainsi qu'une forme de réponse à
 * traiter, plutôt qu'une clé tantôt présente tantôt absente.
 */
export interface FraisBancaires {
  /** Ce que la cotisation coûte, tel que le club l'a fixé. */
  voulu: number;
  /** Ce que la famille règle réellement par carte. */
  demande: number;
  /** L'écart entre les deux : la commission, en centimes. */
  frais: number;
  /** Vraie si le club a choisi de reporter la commission sur le payeur. */
  reportes: boolean;
}

export async function fetchCotisation(reference: string): Promise<{
  success: boolean;
  cotisation: {
    association: string | null;
    contact: string | null;
    adherent: string | null;
    saison: string | null;
    discipline: string | null;
    categorie: string | null;
    montant: number;
    regle: number;
    reste: number;
    soldee: boolean;
    echeance?: string | null;
    paiementEnLigne?: boolean;
    fraisBancaires: FraisBancaires;
    echeancier?: Array<{ rang: number; montant: number; date: string; statut?: string }>;
    etalements?: Array<{ nombre: number; libelle: string; echeances: Array<{ rang: number; montant: number; date: string }> }>;
  };
}> {
  return apiFetch(`/cotisation/${reference}`);
}

export async function ouvrirPaiementCotisation(reference: string) {
  return apiFetch(`/cotisation/${reference}/paiement`, { method: 'POST' });
}

/**
 * Ouvre la signature d'un mandat de prélèvement.
 *
 * Ce n'est pas un paiement : la famille autorise le club à prélever, et le
 * premier débit part ensuite. La banque met quelques jours à le confirmer.
 */
export async function ouvrirPrelevementCotisation(reference: string, echeances: number) {
  return apiFetch(`/cotisation/${reference}/prelevement`, {
    method: 'POST',
    body: JSON.stringify({ echeances }),
  });
}

/** La configuration du registre, réglée depuis les Paramètres. */
export async function fetchConfigurationAdherents() {
  return apiFetch('/adherents/configuration');
}

export async function updateConfigurationAdherents(configuration: unknown) {
  return apiFetch('/adherents/configuration', {
    method: 'PUT',
    body: JSON.stringify({ configuration }),
  });
}

/** Les équipes du club, telles que le village peut les voir. */
export async function fetchEquipesPubliques() {
  return apiFetch('/equipes/public');
}

/* ------------------------- Encaissement en ligne ------------------------ */

/** Les virements reçus, et ce à quoi ils correspondent. */
export async function fetchEncaissements(etat = 'a_rapprocher') {
  return apiFetch(`/bureau/encaissements?etat=${encodeURIComponent(etat)}`);
}

/**
 * Enregistre un virement relevé sur le compte.
 *
 * Recopier le libellé en entier n'est pas une formalité : c'est là que se
 * trouve la référence, et c'est elle qui retrouve la pièce toute seule.
 */
export async function creerEncaissement(saisie: {
  date: string;
  montant: number;
  emetteur: string;
  libelle: string;
}) {
  return apiFetch('/bureau/encaissements', { method: 'POST', body: JSON.stringify(saisie) });
}

export async function rapprocherEncaissement(id: number) {
  return apiFetch(`/bureau/encaissements/${id}/rapprocher`, { method: 'POST' });
}

export async function detacherEncaissement(id: number) {
  return apiFetch(`/bureau/encaissements/${id}/detacher`, { method: 'POST' });
}

export async function ecarterEncaissement(id: number, motif: string) {
  return apiFetch(`/bureau/encaissements/${id}/ecarter`, { method: 'POST', body: JSON.stringify({ motif }) });
}

/**
 * Annule un encaissement saisi à la main — sans l'effacer.
 *
 * Seules les saisies manuelles : un virement venu de la banque s'écarte, il ne
 * s'annule pas. Le relevé dit ce qu'il dit, et nous ne le réécrivons pas.
 */
export async function annulerEncaissement(id: number, motif: string) {
  return apiFetch(`/bureau/encaissements/${id}/annuler`, {
    method: 'POST',
    body: JSON.stringify({ motif }),
  });
}

/* ------------------------- Le tableau du registre ---------------------- */

/**
 * Où en est le club, et ce qui attend quelqu'un.
 *
 * Un seul appel plutôt que six : l'écran répond à six questions, et six appels
 * feraient apparaître les cartes l'une après l'autre — ce qui donne
 * l'impression d'une page qui rame là où le serveur, lui, a tout compté d'un
 * coup.
 */
export interface TableauRegistre {
  success: boolean;
  saison: {
    id: number;
    libelle: string;
    debut: string | null;
    fin: string | null;
    licencies: number;
  } | null;
  message?: string;
  saisons?: Array<{ id: number; libelle: string; courante: boolean }>;
  disciplines?: Array<{ id: number; nom: string }>;
  argent: {
    du: number;
    encaisse: number;
    reste: number;
    cotisationsImpayees: number;
    enRetard: number;
  };
  dossiers: {
    incomplets: number;
    reclame: number;
    apercu: Array<{ id: number; nomComplet: string; manquants: string[] }>;
  };
  licencesEnAttente: {
    total: number;
    apercu: Array<{
      id: number;
      adherentId: number | null;
      nomComplet: string | null;
      discipline: string | null;
      sansNumero: boolean;
    }>;
  };
  groupes: {
    total: number;
    vides: number;
    sansDiscipline: number;
    apercu: Array<{ id: number; nom: string }>;
  };
  calendrier: {
    sansGroupe: number;
    prochaines: Array<{
      id: number;
      debut: string | null;
      aDomicile: boolean;
      adversaire: string;
      lieu: string | null;
      discipline: string | null;
      equipe: string | null;
      convoques: number;
    }>;
  };
  rentree: {
    saisonPrecedente: string | null;
    total: number;
    apercu: Array<{
      id: number;
      nomComplet: string;
      discipline: string | null;
      email: string | null;
      telephone: string | null;
    }>;
  };
}

export async function fetchTableauRegistre(saison?: number): Promise<TableauRegistre> {
  return apiFetch(
    saison === undefined
      ? '/bureau/registre/tableau-de-bord'
      : `/bureau/registre/tableau-de-bord?saison=${saison}`,
  );
}

/* ----------------------------- Rencontres ------------------------------ */

/** Une place dans le groupe, telle que le bureau la lit et l'écrit. */
export interface PlaceRencontre {
  adherent: number | null;
  nom: string | null;
  statut: string;
  role: string | null;
  note: string | null;
}

/** Une rencontre du calendrier. */
export interface Rencontre {
  id: number;
  debut: string | null;
  aDomicile: boolean;
  adversaire: string;
  competition: string | null;
  lieu: string | null;
  discipline: string | null;
  disciplineId: number | null;
  equipe: string | null;
  equipeId: number | null;
  scoreNous: number | null;
  scoreEux: number | null;
  /** « 3 – 1 », ou null tant qu'aucun score n'est saisi. */
  score: string | null;
  /** « gagnee », « nulle », « perdue » — null tant qu'elle n'est pas jouée. */
  issue: string | null;
  statut: string;
  aVenir: boolean;
  commentaire: string | null;
  /** Le groupe convoqué. Rendu au bureau, jamais aux familles. */
  groupe?: PlaceRencontre[];
}

/**
 * Un licencié de la saison, tel que les écrans de composition le proposent.
 *
 * Un adhérent licencié dans deux disciplines apparaît DEUX FOIS — une par
 * licence. C'est voulu : les écrans filtrent par discipline, et le même enfant
 * doit être proposé au judo comme à la gymnastique.
 */
export interface LicencieAligner {
  adherentId: number;
  nomComplet: string;
  categorie: string | null;
  disciplineId: number | null;
  discipline: string | null;
  /** Ce qu'il reste à régler, en centimes. Absent hors de l'écran des équipes. */
  reste?: number;
  statut?: string;
}

/** Une équipe et la liste de ses joueurs, pour convoquer d'un clic. */
export interface EquipeComposee {
  id: number;
  nom: string;
  categorie: string | null;
  disciplineId: number | null;
  discipline: string | null;
  /** Les identifiants d'adhérent, dans l'ordre de la composition. */
  joueurs: number[];
}

export async function fetchRencontres(discipline?: number): Promise<{
  success: boolean;
  saison?: { id: number; libelle: string };
  rencontres: Rencontre[];
  bilan: { jouees: number; gagnees: number; nulles: number; perdues: number; aVenir: number };
  disciplines?: { id: number; nom: string }[];
  /** Les équipes de la saison, avec leur composition : « prendre l'équipe U15 ». */
  equipes?: EquipeComposee[];
  /** Qui est licencié cette saison — donc qui est assuré, donc qui peut être aligné. */
  licencies?: LicencieAligner[];
  message?: string;
}> {
  return apiFetch(
    discipline === undefined ? '/bureau/rencontres' : `/bureau/rencontres?discipline=${discipline}`,
  );
}

export async function creerRencontre(
  donnees: Record<string, unknown>,
): Promise<{ success: boolean; message?: string; rencontre?: Rencontre }> {
  return apiFetch('/bureau/rencontres', { method: 'POST', body: JSON.stringify(donnees) });
}

export async function modifierRencontre(
  id: number,
  donnees: Record<string, unknown>,
): Promise<{ success: boolean; message?: string; rencontre?: Rencontre }> {
  return apiFetch(`/bureau/rencontres/${id}`, { method: 'PUT', body: JSON.stringify(donnees) });
}

export async function supprimerRencontre(
  id: number,
): Promise<{ success: boolean; message?: string }> {
  return apiFetch(`/bureau/rencontres/${id}`, { method: 'DELETE' });
}

/**
 * Compose le groupe d'une rencontre.
 *
 * La liste part ENTIÈRE : l'entraîneur coche son groupe d'un coup le jeudi
 * soir, et le corrige le lundi. Un ajout-retrait ligne par ligne finit
 * toujours par oublier quelqu'un — celui qu'on avait décoché puis recoché.
 *
 * Le serveur garde le statut et la note de ceux qui restent : ajouter un
 * remplaçant ne remet pas les autres à « convoqué ».
 */
export async function convoquerRencontre(
  id: number,
  joueurs: Array<{ adherent: number; statut?: string; role?: string | null; note?: string | null }>,
): Promise<{ success: boolean; message?: string; rencontre?: Rencontre }> {
  return apiFetch(`/bureau/rencontres/${id}/joueurs`, {
    method: 'PUT',
    body: JSON.stringify({ joueurs }),
  });
}

/* ---------------------------- Mon adhésion ----------------------------- */

/**
 * Demande de réinscription pour la saison suivante.
 *
 * Rien n'est encaissé : la licence naît « en attente », au tarif du club pour
 * la catégorie d'âge — recalculée, puisque l'adhérent a un an de plus.
 *
 * `discipline` n'est nécessaire que dans une association omnisports ; ailleurs
 * le serveur prend la seule en service.
 */
export async function renouvelerMonAdhesion(adherent: number, discipline?: number) {
  return apiFetch(`/mon-adhesion/${adherent}/renouveler`, {
    method: 'POST',
    body: JSON.stringify(discipline === undefined ? {} : { sportPreset: discipline }),
  });
}

/** L'espace d'une famille : ses cotisations, ses pièces, son équipe. */

export async function fetchMonAdhesion() {
  return apiFetch('/mon-adhesion');
}

export async function deposerMaPiece(adherent: number, formulaire: FormData) {
  const reponse = await fetch(`${API_BASE}/mon-adhesion/${adherent}/documents`, {
    method: 'POST',
    credentials: 'include',
    body: formulaire,
  });
  const donnees = await reponse.json().catch(() => ({}));
  if (!reponse.ok) throw new Error(donnees.message || `Erreur ${reponse.status}`);
  return donnees;
}

/* ------------------------------- Équipes ------------------------------- */

export async function fetchEquipes(saison?: number | string) {
  return apiFetch(`/bureau/equipes${saison ? `?saison=${saison}` : ''}`);
}

export async function createEquipe(donnees: Record<string, unknown>) {
  return apiFetch('/bureau/equipes', { method: 'POST', body: JSON.stringify(donnees) });
}

export async function updateEquipe(id: number, donnees: Record<string, unknown>) {
  return apiFetch(`/bureau/equipes/${id}`, { method: 'PUT', body: JSON.stringify(donnees) });
}

export async function deleteEquipe(id: number) {
  return apiFetch(`/bureau/equipes/${id}`, { method: 'DELETE' });
}

/** La composition part d'un bloc : l'entraîneur déplace plusieurs noms d'un geste. */
export async function updateComposition(id: number, composition: unknown[]) {
  return apiFetch(`/bureau/equipes/${id}/composition`, { method: 'PUT', body: JSON.stringify({ composition }) });
}

export function urlFeuilleEquipe(id: number): string {
  return `${API_BASE}/bureau/equipes/${id}/feuille`;
}

/* --------------------------- Espace et secours -------------------------- */

/** Remet la base dans l'état d'une nuit précise. Président seulement. */
export async function restaurerSauvegarde(date: string, motDePasse: string) {
  return apiFetch('/admin/sauvegardes/restaurer', {
    method: 'POST',
    body: JSON.stringify({ date, motDePasse }),
  });
}

/** Rejoue les migrations en attente après une restauration ancienne. */
export async function mettreAJourSchema() {
  return apiFetch('/admin/sauvegardes/mettre-a-jour', { method: 'POST' });
}

/** Les sauvegardes de l'instance et la place occupée. */
export async function fetchSauvegardes() {
  return apiFetch('/admin/sauvegardes');
}

/** L'état des modules optionnels : catalogue de la boutique + état local. */
export async function fetchModulesAdmin() {
  return apiFetch('/admin/modules');
}

/** Lance la souscription de modules : rend l'URL Stripe où régler. */
export async function commanderModulesAdmin(codes: string[], periodicite: 'mensuel' | 'annuel') {
  return apiFetch('/admin/modules/commander', {
    method: 'POST',
    body: JSON.stringify({ codes, periodicite }),
  });
}

/**
 * Garde ce module, ou l'écarte.
 *
 * Écarter le ferme dans l'espace et le retire des prochaines échéances. Ce
 * qui est déjà réglé pour la période en cours reste ouvert jusqu'à son terme.
 */
export async function garderModuleAdmin(code: string, garder: boolean) {
  return apiFetch(`/admin/modules/${code}/garder`, {
    method: 'POST',
    body: JSON.stringify({ garder }),
  });
}

/** Ce que la résiliation entraînerait : jusqu'à quand le service tient. */
export async function fetchResiliation() {
  return apiFetch('/admin/resiliation');
}

/**
 * Résilie l'abonnement.
 *
 * Le mot de passe est redemandé : une décision de cette portée ne s'appuie
 * pas sur une session laissée ouverte sur l'ordinateur du club.
 */
export async function resilierAbonnement(password: string, motif: string) {
  return apiFetch('/admin/resiliation', {
    method: 'POST',
    body: JSON.stringify({ password, motif }),
  });
}

/** Ce qui sera reconduit à la prochaine échéance, et à quel prix. */
export async function fetchRenouvellement() {
  return apiFetch('/admin/modules/renouvellement');
}

/**
 * Arrête la liste des modules gardés et ouvre le paiement.
 *
 * Le coupon n'accompagne la demande que s'il y en a un : c'est celui de
 * l'offre de retour faite à une association résiliée, apporté par le lien du
 * courriel. La boutique le valide — jamais le navigateur.
 */
export async function ouvrirRenouvellement(codes: string[], coupon?: string) {
  return apiFetch('/admin/modules/renouvellement', {
    method: 'POST',
    body: JSON.stringify(coupon ? { codes, coupon } : { codes }),
  });
}

/**
 * Déclare qu'un champ obligatoire restera vide — ou revient sur cette
 * déclaration. La bannière cesse alors de le réclamer.
 */
export async function laisserVierge(cle: string, laisseVierge: boolean) {
  return apiFetch(`/admin/parametres/${cle}`, {
    method: 'PUT',
    body: JSON.stringify({ laisseVierge }),
  });
}

/** Révèle la valeur en clair d'un paramètre sensible après vérification du mot de passe. */
export async function revealParametreAdmin(cle: string, password: string) {
  return apiFetch(`/admin/parametres/reveal/${cle}`, {
    method: 'POST',
    body: JSON.stringify({ password }),
  });
}

export async function testEmailAdmin(email?: string) {
  return apiFetch('/admin/parametres/test-email', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

export async function fetchBureauTableauDeBord() {
  return apiFetch('/bureau/tableau-de-bord');
}

export async function fetchBureauSouscriptions(statut?: string) {
  const query = statut ? `?statut=${encodeURIComponent(statut)}` : '';
  return apiFetch(`/bureau/souscriptions${query}`);
}

export async function fetchBureauFactures(statut?: string) {
  const query = statut ? `?statut=${encodeURIComponent(statut)}` : '';
  return apiFetch(`/bureau/factures${query}`);
}

export async function marquerFacturePayee(id: number, payload: { referencePaiement?: string; datePaiement?: string }) {
  return apiFetch(`/bureau/factures/${id}/marquer-payee`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function annulerSouscription(id: number) {
  return apiFetch(`/bureau/souscriptions/${id}/annuler`, {
    method: 'POST',
  });
}

export async function fetchBenevoleMoi() {
  return apiFetch('/benevole/moi');
}

/** Vêtements proposés et compétences déclarables, définis côté serveur. */
export async function fetchReferentielBenevole() {
  return apiFetch('/benevole/referentiel');
}
export async function updateBenevoleMoi(payload: Record<string, any>) {
  return apiFetch('/benevole/moi', {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

export async function repondrePlanning(id: number, reponse: 'confirme' | 'refuse', commentaire?: string) {
  return apiFetch(`/benevole/plannings/${id}/repondre`, {
    method: 'POST',
    body: JSON.stringify({ reponse, commentaire }),
  });
}

export async function fetchBureauBenevoles() {
  return apiFetch('/bureau/benevoles');
}

export async function fetchEvenementsBureau() {
  return apiFetch('/evenements');
}

/* ------------------------------- Événements ------------------------------- */

export interface EvenementPayload {
  nom: string;
  dateDebut: string;
  dateFin?: string | null;
  lieu?: string | null;
  description?: string | null;
  statut?: string;
}

/** Paramètres du repas sur réservation, portés par l'événement. */
export interface RepasPayload {
  repasActif: boolean;
  repasDescription?: string | null;
  repasPrixAdulte: number;
  repasPrixEnfant: number;
  repasQuotaAdulte: number;
  repasQuotaEnfant: number;
  /** Date et heure du service, obligatoire dès que le repas est ouvert. */
  repasDate?: string | null;
  repasDateLimite?: string | null;
}

/** Paramètres du concert sur réservation, portés par l'événement. */
export interface ConcertPayload {
  concertActif: boolean;
  concertDescription?: string | null;
  concertPrixPlace: number;
  concertQuotaPayant: number;
  /** Places gratuites : elles ne rapportent rien mais occupent un siège. */
  concertQuotaGratuit: number;
  /** Date et heure du concert, obligatoire dès que la billetterie est ouverte. */
  concertDate?: string | null;
  concertDateLimite?: string | null;
}

export async function createEvenement(payload: EvenementPayload) {
  return apiFetch('/evenements', { method: 'POST', body: JSON.stringify(payload) });
}

export async function updateEvenement(
  id: number,
  payload: Partial<EvenementPayload & RepasPayload & ConcertPayload>,
) {
  return apiFetch(`/evenements/${id}`, { method: 'PUT', body: JSON.stringify(payload) });
}

export async function deleteEvenement(id: number) {
  return apiFetch(`/evenements/${id}`, { method: 'DELETE' });
}

/* ---------------------------- Membres du bureau ---------------------------- */

export async function fetchRolesBureau() {
  return apiFetch('/bureau/roles-bureau');
}

export async function fetchBureauMembres(type?: 'bureau' | 'benevole') {
  const query = type ? `?type=${type}` : '';
  return apiFetch(`/bureau/membres${query}`);
}

/**
 * Crée un compte bénévole ou membre du bureau. Le backend envoie ensuite
 * l'email invitant la personne à définir son mot de passe.
 */
export async function createBureauMembre(payload: {
  prenom: string;
  nom: string;
  email: string;
  telephone?: string;
  type: 'benevole' | 'bureau';
  bureauRoleId?: number | null;
}) {
  return apiFetch('/bureau/membres', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function updateBureauMembre(id: number, payload: Record<string, any>) {
  return apiFetch(`/bureau/membres/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

export async function renvoyerInvitationMembre(id: number) {
  return apiFetch(`/bureau/membres/${id}/renvoyer-invitation`, {
    method: 'POST',
  });
}

/** Envoie un lien de réinitialisation de mot de passe à un membre (admin/président). */
export async function reinitialiserMotDePasseMembre(id: number) {
  return apiFetch(`/bureau/membres/${id}/reinitialiser-mot-de-passe`, {
    method: 'POST',
  });
}

export async function deleteBureauMembre(id: number) {
  return apiFetch(`/bureau/membres/${id}`, {
    method: 'DELETE',
  });
}

/**
 * URL de téléchargement d'un document. Le backend régénère le PDF s'il a
 * disparu du disque : un lien direct vers le fichier tomberait sur la règle de
 * réécriture du frontal et renverrait la page React au lieu du document.
 */
export function urlPdfDevis(id: number) {
  return `${API_BASE}/bureau/devis/${id}/pdf`;
}

export function urlPdfFacture(id: number) {
  return `${API_BASE}/bureau/factures/${id}/pdf`;
}

/* ----------------------------- Mon compte -------------------------------- */

/** Le mot de passe actuel est exigé : une session ouverte ne suffit pas. */
export async function changerEmail(email: string, currentPassword: string) {
  return apiFetch('/me/email', {
    method: 'PUT',
    body: JSON.stringify({ email, currentPassword }),
  });
}

export async function changerMotDePasse(
  currentPassword: string,
  newPassword: string,
  newPasswordConfirmation: string,
) {
  return apiFetch('/me/password', {
    method: 'PUT',
    body: JSON.stringify({ currentPassword, newPassword, newPasswordConfirmation }),
  });
}

/** Modifie les coordonnées du profil (téléphone, photo, anonyme). */
export async function modifierProfil(payload: { telephone?: string | null; photoUrl?: string | null; anonyme?: boolean; newsletter?: boolean }) {
  return apiFetch('/me/profil', {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

/** Page publique Association : organigramme du bureau et bénévoles. */
export async function fetchAssociationPublic() {
  return apiFetch('/association/public');
}

/* ------------------------------ Référentiels ------------------------------ */

export interface ReferentielItem {
  id: number;
  code: string;
  label: string;
  actif: boolean;
  niveau?: number;
  ordre?: number;
  utilisations?: number;
  dirigeante?: boolean;
  /** Codes des droits attribués à la fonction, pour la grille. */
  droits?: string[];
}

/** Une famille de droits et son contenu, telle que le serveur la décrit. */
export interface FamilleDroits {
  code: string;
  libelle: string;
  droits: { code: string; libelle: string; aide: string }[];
}

export async function fetchReferentiels() {
  return apiFetch('/admin/referentiels');
}

export async function createRoleBureau(payload: { label: string; niveau?: number }) {
  return apiFetch('/admin/referentiels/roles-bureau', { method: 'POST', body: JSON.stringify(payload) });
}

export async function updateRoleBureau(id: number, payload: Record<string, unknown>) {
  return apiFetch(`/admin/referentiels/roles-bureau/${id}`, { method: 'PUT', body: JSON.stringify(payload) });
}

export async function deleteRoleBureau(id: number) {
  return apiFetch(`/admin/referentiels/roles-bureau/${id}`, { method: 'DELETE' });
}

export async function createSecteur(payload: { label: string; ordre?: number }) {
  return apiFetch('/admin/referentiels/secteurs', { method: 'POST', body: JSON.stringify(payload) });
}

export async function updateSecteur(id: number, payload: Record<string, unknown>) {
  return apiFetch(`/admin/referentiels/secteurs/${id}`, { method: 'PUT', body: JSON.stringify(payload) });
}

export async function deleteSecteur(id: number) {
  return apiFetch(`/admin/referentiels/secteurs/${id}`, { method: 'DELETE' });
}

/* -------------------------------- Annuaire -------------------------------- */

/** Annuaire en lecture seule, accessible à tout bénévole. */
export async function fetchAnnuaireMembres() {
  return apiFetch('/annuaire/membres');
}

/* ------------------------- Facturation (bureau) -------------------------- */

export interface LigneDocument {
  designation: string;
  quantite: number;
  prixUnitaire: number;
  total?: number;
}

export interface DocumentPayload {
  sponsorId?: number | null;
  sponsorNom?: string;
  sponsorEmail?: string;
  sponsorAdresse?: string;
  sponsorSiret?: string;
  sponsorContactNom?: string;
  sponsorContactPrenom?: string;
  sponsorTelephone?: string;
  modePaiement?: string;
  lignes: LigneDocument[];
  /**
   * Rattacher la pièce à l'exercice rouvert plutôt qu'à l'année en cours.
   * Seules ces deux cibles existent — le serveur retombe sur l'année en
   * cours pour tout autre identifiant.
   */
  exerciceId?: number;
}

export async function createBureauDevis(payload: DocumentPayload) {
  return apiFetch('/bureau/devis', { method: 'POST', body: JSON.stringify(payload) });
}

export async function updateBureauDevis(id: number, payload: Partial<DocumentPayload>) {
  return apiFetch(`/bureau/devis/${id}`, { method: 'PUT', body: JSON.stringify(payload) });
}

export async function facturerBureauDevis(id: number, modePaiement?: string) {
  return apiFetch(`/bureau/devis/${id}/facturer`, {
    method: 'POST',
    body: JSON.stringify({ modePaiement }),
  });
}

export async function createBureauFacture(payload: DocumentPayload) {
  return apiFetch('/bureau/factures', { method: 'POST', body: JSON.stringify(payload) });
}

/** Annule une facture émise par un avoir : une facture ne se modifie jamais. */
export async function emettreAvoir(id: number) {
  return apiFetch(`/bureau/factures/${id}/avoir`, { method: 'POST' });
}

/** Corrige le nom du destinataire d'une facture (coquille) : fiche client mise à jour, PDF regénéré. */
export async function corrigerDestinataireFacture(id: number, nom: string) {
  return apiFetch(`/bureau/factures/${id}/corriger-destinataire`, {
    method: 'POST',
    body: JSON.stringify({ nom }),
  });
}

/* --------------------------- Mot de passe oublié -------------------------- */

/**
 * Demande un lien de réinitialisation. La réponse est volontairement identique
 * que le compte existe ou non : elle ne doit rien révéler sur les inscrits.
 */
export async function demanderReinitialisation(email: string) {
  return apiFetch('/mot-de-passe-oublie', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

export async function verifierJetonReinitialisation(token: string) {
  return apiFetch(`/mot-de-passe-oublie/${encodeURIComponent(token)}`);
}

export async function reinitialiserMotDePasse(
  token: string,
  password: string,
  passwordConfirmation: string,
) {
  return apiFetch(`/mot-de-passe-oublie/${encodeURIComponent(token)}`, {
    method: 'POST',
    body: JSON.stringify({ password, passwordConfirmation }),
  });
}

/* ------------------------------- Invitation ------------------------------- */

export async function verifierInvitation(token: string) {
  return apiFetch(`/invitation/${encodeURIComponent(token)}`);
}

export async function definirMotDePasse(token: string, password: string, passwordConfirmation: string) {
  return apiFetch(`/invitation/${encodeURIComponent(token)}`, {
    method: 'POST',
    body: JSON.stringify({ password, passwordConfirmation }),
  });
}

export async function uploadFichierSponsor(
  souscriptionId: number,
  file: File,
  type: 'visuel' | 'logo_hd' | 'document' = 'visuel'
) {
  const base64 = await fileToBase64(file);

  const response = await fetch(`/api/sponsors/public/${souscriptionId}/upload-base64`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({
      nomOriginal: file.name,
      mimeType: file.type || 'application/octet-stream',
      base64,
      type,
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || `Erreur ${response.status}`);
  }

  return response.json();
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Retire le prefixe data:[mime];base64,
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Détail d'un événement publié, pour sa page dédiée.
 */
export async function fetchEvenementPublic(id: number | string) {
  const data = await apiFetch(`/evenements/public/${id}`);
  return data.evenement ?? data;
}

export interface EntrepriseTrouvee {
  siren: string | null;
  siret: string | null;
  /** Raison sociale ou dénomination sociale. */
  nom: string | null;
  adresse: string | null;
  codePostal: string | null;
  ville: string | null;
  /** Code NAF de l'activité principale, par exemple « 70.10Z ». */
  codeNaf: string | null;
  /** Libellé de ce code, traduit par le référentiel de l'INSEE. */
  activite: string | null;
  codeFormeJuridique: string | null;
  formeJuridique: string | null;
  /** Première immatriculation, au format ISO. */
  dateImmatriculation: string | null;
  /** Déduit du SIREN : n'a de sens que pour une entreprise assujettie. */
  tvaIntracommunautaire: string | null;
  active: boolean;
}

/**
 * Coordonnées d'une entreprise d'après son SIREN ou SIRET.
 *
 * Renvoie null dès que rien n'est trouvé — y compris quand le répertoire est
 * indisponible : la recherche est un confort, elle ne doit jamais empêcher de
 * remplir le formulaire à la main.
 */
export async function rechercherEntreprise(numero: string): Promise<EntrepriseTrouvee | null> {
  const chiffres = numero.replace(/\D/g, '');
  if (chiffres.length !== 9 && chiffres.length !== 14) return null;

  try {
    const data = await apiFetch(`/entreprise/${chiffres}`);
    return data.trouve ? (data.entreprise as EntrepriseTrouvee) : null;
  } catch {
    return null;
  }
}

/* ===================== Événements : visuel, participants, planning ===================== */

/** Listes de référence pour les menus déroulants des écrans d'événement. */
export async function fetchEvenementReferentiel(evenementId: number) {
  return apiFetch(`/evenements/${evenementId}/referentiel`);
}

/**
 * Envoie le visuel d'un événement.
 *
 * Requête multipart : on ne passe pas par apiFetch, qui impose un
 * Content-Type JSON et empêcherait le navigateur de poser sa frontière.
 */
export async function envoyerVisuelEvenement(evenementId: number, fichier: File) {
  const corps = new FormData();
  corps.append('image', fichier);

  const response = await fetch(`${API_BASE}/evenements/${evenementId}/visuel`, {
    method: 'POST',
    credentials: 'include',
    body: corps,
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.message || `Erreur ${response.status}`);
  return data;
}

export async function retirerVisuelEvenement(evenementId: number) {
  return apiFetch(`/evenements/${evenementId}/visuel`, { method: 'DELETE' });
}

export interface ParticipantPayload {
  membreId?: number;
  type: string;
  statut: string;
  activitySectorId?: number | null;
  bureauRoleId?: number | null;
  commentaire?: string | null;
}

export async function fetchParticipants(evenementId: number) {
  const data = await apiFetch(`/evenements/${evenementId}/participants`);
  return data.participants ?? [];
}

export async function createParticipant(evenementId: number, payload: ParticipantPayload) {
  return apiFetch(`/evenements/${evenementId}/participants`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function updateParticipant(
  evenementId: number,
  id: number,
  payload: ParticipantPayload
) {
  return apiFetch(`/evenements/${evenementId}/participants/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

export async function deleteParticipant(evenementId: number, id: number) {
  return apiFetch(`/evenements/${evenementId}/participants/${id}`, { method: 'DELETE' });
}

export interface CreneauPayload {
  benevoleId?: number;
  eventParticipantId?: number | null;
  mission: string;
  statutConfirmation: string;
  debut: string;
  fin: string;
  commentaire?: string | null;
  /**
   * Passer outre un conflit d'horaire deja signale.
   *
   * Le serveur refuse par defaut d'affecter un benevole sur deux creneaux qui
   * se chevauchent. Ce drapeau n'est pose qu'apres que l'ecran a montre le
   * conflit et que quelqu'un a dit oui : le bureau sait parfois ce qu'il fait,
   * mais il doit l'avoir vu.
   */
  forcer?: boolean;
}

export async function fetchPlanningEvenement(evenementId: number) {
  const data = await apiFetch(`/evenements/${evenementId}/planning`);
  return data.creneaux ?? [];
}

export async function createCreneau(evenementId: number, payload: CreneauPayload) {
  return apiFetch(`/evenements/${evenementId}/planning`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function updateCreneau(evenementId: number, id: number, payload: CreneauPayload) {
  return apiFetch(`/evenements/${evenementId}/planning/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

export async function deleteCreneau(evenementId: number, id: number) {
  return apiFetch(`/evenements/${evenementId}/planning/${id}`, { method: 'DELETE' });
}


/** Crée un compte sponsor (identifiant et mot de passe) depuis le formulaire public. */
export async function creerCompteSponsor(payload: {
  email: string;
  password: string;
  passwordConfirmation: string;
  prenom?: string;
  nom?: string;
}) {
  return apiFetch('/sponsors/public/compte', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/* --------------------------------- Dons libres -------------------------------- */

export async function fetchDonsModes() {
  return apiFetch('/dons/modes');
}

/**
 * Enregistre un don et, pour la carte bancaire, ouvre le paiement.
 *
 * `url` est nulle pour les autres moyens : rien n'est encaissé en ligne, le
 * bureau rapproche à la main. Un reçu nominatif suppose les coordonnées
 * complètes, sans lesquelles l'API refuse.
 */
export async function soumettreDon(payload: {
  prenom?: string;
  nom?: string;
  anonyme: boolean;
  souhaiteRecu?: boolean;
  email?: string;
  adresse?: string;
  codePostal?: string;
  ville?: string;
  montant: number;
  modePaiement: string;
}): Promise<{ success: boolean; message: string; don: any; url: string | null }> {
  return apiFetch('/dons/public/soumettre', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/**
 * État d'un don au retour du paiement.
 *
 * Porte le jeton du justificatif tant qu'il n'a pas été consommé : c'est lui
 * qui autorise l'unique téléchargement d'un don anonyme.
 */
export async function fetchEtatDon(reference: string) {
  return apiFetch(`/dons/public/${encodeURIComponent(reference)}`);
}

export async function fetchBureauDons(statut?: string) {
  const query = statut ? `?statut=${encodeURIComponent(statut)}` : '';
  return apiFetch(`/bureau/dons${query}`);
}

export async function marquerDonPaye(id: number) {
  return apiFetch(`/bureau/dons/${id}/marquer-paye`, {
    method: 'POST',
  });
}

/* ------------------------------- Photo de profil ------------------------------ */

/**
 * Envoie une image en multipart : le Content-Type est laissé au navigateur,
 * qui seul connaît la frontière (« boundary ») séparant les parties.
 */
async function apiUpload(path: string, formData: FormData) {
  const response = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: { Accept: 'application/json' },
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || `Erreur ${response.status}`);
  }

  return response.json();
}

/** Envoie ou remplace la photo de profil du membre connecté. */
export async function envoyerPhotoProfil(fichier: File) {
  const formData = new FormData();
  formData.append('photo', fichier);

  return apiUpload('/me/photo', formData);
}

/** Retire la photo de profil du membre connecté. */
export async function retirerPhotoProfil() {
  return apiFetch('/me/photo', { method: 'DELETE' });
}

/* ------------------------------ Sponsors — bureau ----------------------------- */

export interface SponsorChoix {
  support: string;
  libelle: string;
  montant: number;
}

export interface SponsorListe {
  id: number;
  nom: string | null;
  statut: string | null;
  siret: string | null;
  activite: string | null;
  contact: string | null;
  email: string | null;
  telephone: string | null;
  adresse: string | null;
  siteWeb: string | null;
  choix: SponsorChoix[];
  nbSouscriptions: number;
  montantN: number;
  montantN1: number;
  souscritN: number;
  souscritN1: number;
}

/** Annuaire des sponsors avec montants de l'exercice courant et du précédent. */
export async function fetchBureauSponsors(): Promise<{
  success: boolean;
  anneeN: number;
  anneeN1: number;
  sponsors: SponsorListe[];
}> {
  return apiFetch('/bureau/sponsors');
}

/** Fiche détaillée : suivi financier, documents comptables et visuels. */
export async function fetchBureauSponsorFiche(id: number) {
  return apiFetch(`/bureau/sponsors/${id}`);
}

/* --------------------------------- Exercices ---------------------------------- */

export interface Exercice {
  id: number;
  annee: number;
  cloture: boolean;
  clotureLe: string | null;
  createdAt: string | null;
  /** Date de refermeture d'un exercice rouvert ; null s'il ne l'est pas. */
  reouvertJusquA?: string | null;
  /** L'exploitant a déverrouillé : le bureau peut rouvrir. */
  reouvertureAutorisee?: boolean;
}

/** Chiffres de l'exercice courant (N) et du précédent (N-1). */
export async function fetchExerciceChiffres(): Promise<{
  success: boolean;
  exerciceCourant: Exercice | null;
  anneeN: number;
  anneeN1: number;
  chiffreN: number;
  chiffreN1: number;
  /** Part des dons dans le chiffre : le total additionne parrainages et dons. */
  donsN: number;
  donsN1: number;
  /** Repas et concerts encaissés : la billetterie compte dans le chiffre. */
  billetterieN: number;
  billetterieN1: number;
  souscritN: number;
  souscritN1: number;
  variation: number | null;
  exercicePrecedentConnu: boolean;
  peutCloturer: boolean;
  estSuperAdmin: boolean;
  /** Ce que le bouton de clôture doit dire, et s'il doit répondre au clic. */
  cloture: {
    mode: 'annuelle' | 'calendaire' | 'desactivee';
    libelle: string;
    echeance: string | null;
    manuelleAutorisee: boolean;
  };
  /** L'exercice en fenêtre de réouverture, cible possible d'une pièce nouvelle. */
  rouvert: { id: number; annee: number; jusquA: string | null } | null;
}> {
  return apiFetch('/bureau/exercices/chiffres');
}

/** Exercices clos, du plus récent au plus ancien. */
export async function fetchExerciceArchives(): Promise<{
  success: boolean;
  archives: (Exercice & { chiffre: number; souscrit: number })[];
  estSuperAdmin: boolean;
  peutSaisirAnterieur: boolean;
  anneeAnterieureProposee: number;
  joursReouverture: number;
}> {
  return apiFetch('/bureau/exercices/archives');
}

/** Clôture l'exercice ouvert et démarre le suivant. Irréversible. */
export async function cloturerExercice() {
  return apiFetch('/bureau/exercices/cloturer', { method: 'POST' });
}

/** Inscrit un exercice passé, déjà clos, pour y porter les chiffres de N-1. */
export async function creerExerciceAnterieur(annee: number) {
  return apiFetch('/bureau/exercices/anterieur', {
    method: 'POST',
    body: JSON.stringify({ annee }),
  });
}

/** Rouvre un exercice clos pour vingt-et-un jours. Super administrateur seulement. */
export async function rouvrirExercice(id: number) {
  return apiFetch(`/bureau/exercices/${id}/rouvrir`, { method: 'POST' });
}

/** Ouvre ou referme le bouton de clôture manuelle. Super administrateur seulement. */
export async function autoriserClotureManuelle(autorisee: boolean) {
  return apiFetch('/bureau/exercices/cloture-manuelle', {
    method: 'POST',
    body: JSON.stringify({ autorisee }),
  });
}


/* --------------------------------- Réunions ---------------------------------- */

export interface ReunionCreneau {
  id: number;
  debut: string;
  fin: string;
  oui: number;
  peutEtre: number;
  non: number;
  score: number;
  maReponse: string | null;
  reponses: Array<{ membre: string | null; disponibilite: string }>;
  retenu: boolean;
}

export interface ReunionDocument {
  url: string;
  nom: string | null;
  mimeType: string | null;
  taille: number | null;
}

export interface Reunion {
  id: number;
  titre: string;
  type: string;
  typeLabel: string;
  statut: string;
  statutLabel: string;
  lieu: string | null;
  ordreDuJour: string | null;
  sondageOuvert: boolean;
  sondageFermeLe: string | null;
  sondageClotureLe: string | null;
  dateRetenue: string | null;
  finRetenue: string | null;
  compteRendu: string | null;
  compteRenduPublieLe: string | null;
  /** Document joint, quand le compte rendu vient d'ailleurs. */
  compteRenduFichier: ReunionDocument | null;
  auteur: string | null;
  createdAt: string | null;
  creneaux: ReunionCreneau[];
}

export interface ReunionPayload {
  titre: string;
  type: string;
  lieu?: string | null;
  ordreDuJour?: string | null;
  sondageFermeLe?: string | null;
  creneaux?: Array<{ id?: number; debut: string; fin: string }>;
  notifier?: boolean;
}

/** Types de réunion, statuts et disponibilités, définis côté serveur. */
export async function fetchReferentielReunions() {
  return apiFetch('/reunions/referentiel');
}

export async function fetchReunions(): Promise<{
  success: boolean;
  peutGerer: boolean;
  reunions: Reunion[];
}> {
  return apiFetch('/reunions');
}

export async function creerReunion(payload: ReunionPayload) {
  return apiFetch('/reunions', { method: 'POST', body: JSON.stringify(payload) });
}

/** Enregistre les disponibilités du membre connecté : créneau → réponse. */
export async function repondreReunion(id: number, reponses: Record<number, string>) {
  return apiFetch(`/reunions/${id}/repondre`, {
    method: 'POST',
    body: JSON.stringify({ reponses }),
  });
}

/** Arrête la date et envoie la convocation. Sans créneau, le mieux placé gagne. */
export async function cloturerSondageReunion(
  id: number,
  payload: { creneauId?: number | null; lieu?: string | null },
) {
  return apiFetch(`/reunions/${id}/cloturer`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function enregistrerCompteRendu(
  id: number,
  payload: { compteRendu: string | null; diffuser?: boolean },
) {
  return apiFetch(`/reunions/${id}/compte-rendu`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

export async function supprimerReunion(id: number) {
  return apiFetch(`/reunions/${id}`, { method: 'DELETE' });
}


/* ----------------------------- Repas sur réservation -------------------------- */

export interface EtatRepas {
  actif: boolean;
  /** Faux dès que le quota est atteint ou la date limite passée. */
  ouvert: boolean;
  complet: boolean;
  description: string | null;
  /** Date et heure du service. */
  date: string | null;
  dateLimite: string | null;
  prixAdulte: number;
  prixEnfant: number;
  quotaAdulte: number;
  quotaEnfant: number;
  restantAdulte: number;
  restantEnfant: number;
  restantTotal: number;
}

/** Tarifs, quotas et couverts restants d'un événement publié. */
export async function fetchRepasEvenement(evenementId: number): Promise<{
  success: boolean;
  repas: EtatRepas;
}> {
  return apiFetch(`/repas/public/${evenementId}`);
}

/** Crée la réservation et ouvre le paiement Stripe. Renvoie l'URL de règlement. */
export async function reserverRepas(
  evenementId: number,
  payload: {
    nom: string;
    prenom?: string | null;
    email: string;
    telephone?: string | null;
    nbAdultes: number;
    nbEnfants: number;
    commentaire?: string | null;
  },
): Promise<{ success: boolean; reference: string; montant: number; url: string }> {
  return apiFetch(`/repas/public/${evenementId}/reserver`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/** État d'une réservation, consulté au retour du paiement. */
export async function fetchReservationRepas(reference: string) {
  return apiFetch(`/repas/public/reservation/${reference}`);
}

/** Réservations d'un événement, pour le bureau. */
export async function fetchReservationsBureau(evenementId: number) {
  return apiFetch(`/repas/bureau/${evenementId}`);
}

/**
 * Corrige les coordonnées d'une réservation.
 *
 * Les quantités et le montant ne sont pas modifiables : ils correspondent à un
 * paiement déjà encaissé.
 */
export async function modifierReservationRepas(
  id: number,
  data: {
    nom?: string;
    prenom?: string | null;
    email?: string;
    telephone?: string | null;
    commentaire?: string | null;
  }
) {
  return apiFetch(`/repas/bureau/reservation/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

/** Renvoie la confirmation au convive, QR de retrait compris. */
export async function renvoyerConfirmationRepas(id: number) {
  return apiFetch(`/repas/bureau/reservation/${id}/renvoyer`, { method: 'POST' });
}

/* ---------------------------- Concert sur réservation ------------------------- */

export interface EtatConcert {
  actif: boolean;
  /** Faux dès que le quota est atteint ou la date limite passée. */
  ouvert: boolean;
  complet: boolean;
  description: string | null;
  /** Date et heure du concert. */
  date: string | null;
  dateLimite: string | null;
  prixPlace: number;
  quotaPayant: number;
  quotaGratuit: number;
  restantPayant: number;
  restantGratuit: number;
  restantTotal: number;
}

/** Tarif, quotas et places restantes d'un événement publié. */
export async function fetchConcertEvenement(evenementId: number): Promise<{
  success: boolean;
  concert: EtatConcert;
}> {
  return apiFetch(`/concert/public/${evenementId}`);
}

/**
 * Crée la réservation et ouvre le paiement Stripe.
 *
 * `url` est nulle pour une réservation entièrement gratuite : rien à régler,
 * la confirmation est déjà partie.
 */
export async function reserverConcert(
  evenementId: number,
  payload: {
    nom: string;
    prenom?: string | null;
    email: string;
    telephone?: string | null;
    nbPayantes: number;
    nbGratuites: number;
    commentaire?: string | null;
  },
): Promise<{ success: boolean; reference: string; montant: number; url: string | null }> {
  return apiFetch(`/concert/public/${evenementId}/reserver`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/** État d'une réservation, consulté au retour du paiement. */
export async function fetchReservationConcert(reference: string) {
  return apiFetch(`/concert/public/reservation/${encodeURIComponent(reference)}`);
}

/** Réservations de concert d'un événement, pour le bureau. */
export async function fetchReservationsConcertBureau(evenementId: number) {
  return apiFetch(`/concert/bureau/${evenementId}`);
}

/** Corrige les coordonnées d'une réservation de concert. */
export async function modifierReservationConcert(
  id: number,
  data: {
    nom?: string;
    prenom?: string | null;
    email?: string;
    telephone?: string | null;
    commentaire?: string | null;
  }
) {
  return apiFetch(`/concert/bureau/reservation/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

/** Renvoie la confirmation au spectateur, QR d'entrée compris. */
export async function renvoyerConfirmationConcert(id: number) {
  return apiFetch(`/concert/bureau/reservation/${id}/renvoyer`, { method: 'POST' });
}

/** Joint un document au compte rendu d'une réunion. */
export async function joindreDocumentReunion(id: number, fichier: File) {
  const formData = new FormData();
  formData.append('document', fichier);

  return apiUpload(`/reunions/${id}/compte-rendu/document`, formData);
}

export async function retirerDocumentReunion(id: number) {
  return apiFetch(`/reunions/${id}/compte-rendu/document`, { method: 'DELETE' });
}

/* ------------------------ Contrôle des visuels sponsors ----------------------- */

/** Déclare un visuel conforme. Le sponsor en est informé par e-mail. */
export async function validerVisuelSponsor(fichierId: number) {
  return apiFetch(`/bureau/sponsors/visuels/${fichierId}/valider`, { method: 'POST' });
}

/** Refuse un visuel. Le motif est communiqué tel quel au sponsor. */
export async function refuserVisuelSponsor(fichierId: number, motif: string) {
  return apiFetch(`/bureau/sponsors/visuels/${fichierId}/refuser`, {
    method: 'POST',
    body: JSON.stringify({ motif }),
  });
}

/* --------------------------------- Boutique --------------------------------- */

export interface ArticleBoutique {
  id: number;
  nom: string;
  description: string | null;
  prix: number;
  stock: number;
  imageUrl: string | null;
  actif: boolean;
  ordre: number;
  /** Vide pour ce qui n'a pas de taille : la question n'est alors pas posée. */
  tailles: string[];
  /** Ce que l'article pèse dans le colis, en parts. */
  partExpedition: number;
}

/** Un palier de la grille de frais de port. */
export interface PalierFrais {
  jusqua: number | null;
  prix: number;
  libelle: string;
}

export interface CommandeBoutique {
  id: number;
  reference: string;
  clientNom: string;
  clientEmail: string;
  clientTelephone: string | null;
  mode: 'retrait' | 'expedition';
  adresseLivraison: string | null;
  fraisExpedition: number;
  lignes: { goodieId: number; nom: string; taille?: string | null; prixUnitaire: number; quantite: number }[];
  total: number;
  statut: string;
  numeroSuivi: string | null;
  commentaire: string | null;
  createdAt: string;
  transporteur?: string | null;
  /** L'étiquette PDF, si elle a été générée. */
  etiquetteUrl?: string | null;
  /** Ce qui empêche de la générer : une liste, pas un bouton grisé muet. */
  etiquetteManques?: string[];
}

/** Vitrine publique : articles actifs et grille des frais de port. */
export async function fetchBoutiquePublic(): Promise<{
  success: boolean;
  active: boolean;
  articles: ArticleBoutique[];
  fraisGrille?: PalierFrais[];
}> {
  return apiFetch('/boutique/public');
}

/**
 * Ouvre le paiement d'une commande.
 *
 * Tout est expédié, l'adresse est donc obligatoire, et la réponse porte
 * l'adresse de la page de paiement : la commande n'est ferme qu'une fois la
 * carte passée.
 */
export async function commanderBoutique(payload: {
  nom: string;
  email: string;
  telephone?: string;
  /** L'adresse en morceaux : un transporteur ne sait pas lire un pavé. */
  adresseLigne1: string;
  adresseLigne2?: string;
  codePostal: string;
  ville: string;
  commentaire?: string;
  articles: { id: number; taille: string; quantite: number }[];
}) {
  return apiFetch('/boutique/public/commander', { method: 'POST', body: JSON.stringify(payload) });
}

/** Demande l'étiquette d'expédition au transporteur, et son numéro de suivi. */
export async function genererEtiquetteCommande(id: number) {
  return apiFetch(`/bureau/boutique/commandes/${id}/etiquette`, { method: 'POST' });
}

export async function fetchBoutiqueArticles(): Promise<{
  success: boolean;
  /** L'option est-elle souscrite : ce à quoi l'association a droit. */
  active: boolean;
  /** L'association a-t-elle ouvert sa vitrine : ce qu'elle montre. */
  enLigne: boolean;
  /** Les deux à la fois — ce que voit réellement un visiteur. */
  vitrineOuverte: boolean;
  articlesPublies: number;
  /**
   * L'encaissement : sans lui, la vitrine peut ouvrir et rien ne se paie.
   *
   * `modeTest` est le cas le plus trompeur — tout a l'air de fonctionner,
   * la commande s'enregistre, et pas un euro n'arrive.
   */
  encaissement: { configure: boolean; modeTest: boolean };
  articles: ArticleBoutique[];
}> {
  return apiFetch('/bureau/boutique/articles');
}

/**
 * Ouvre ou ferme la vitrine, sans toucher au catalogue.
 *
 * Fermer ne désactive aucun article, rouvrir n'en publie aucun : c'est ce qui
 * permet de préparer une saison entière puis d'ouvrir d'un geste.
 */
export async function basculerVitrineBoutique(enLigne: boolean) {
  return apiFetch('/bureau/boutique/en-ligne', {
    method: 'POST',
    body: JSON.stringify({ enLigne }),
  });
}

export async function creerArticleBoutique(payload: Partial<ArticleBoutique>) {
  return apiFetch('/bureau/boutique/articles', { method: 'POST', body: JSON.stringify(payload) });
}

/**
 * Modifie un article.
 *
 * Les tailles s'acceptent en liste ou en chaîne séparée par des virgules :
 * l'écran les saisit d'une traite, le serveur les découpe.
 */
export async function modifierArticleBoutique(
  id: number,
  payload: Partial<Omit<ArticleBoutique, 'tailles'>> & { tailles?: string[] | string }
) {
  return apiFetch(`/bureau/boutique/articles/${id}`, { method: 'PUT', body: JSON.stringify(payload) });
}

export async function supprimerArticleBoutique(id: number) {
  return apiFetch(`/bureau/boutique/articles/${id}`, { method: 'DELETE' });
}

export async function uploadImageArticleBoutique(id: number, image: File) {
  const donnees = new FormData();
  donnees.append('image', image);
  const reponse = await fetch(`${API_BASE}/bureau/boutique/articles/${id}/image`, {
    method: 'POST',
    body: donnees,
    credentials: 'include',
  });
  const json = await reponse.json().catch(() => ({}));
  if (!reponse.ok) throw new Error(json.message ?? `Erreur ${reponse.status}`);
  return json;
}

export async function fetchBoutiqueCommandes(): Promise<{ success: boolean; commandes: CommandeBoutique[] }> {
  return apiFetch('/bureau/boutique/commandes');
}

export async function modifierCommandeBoutique(id: number, payload: { statut?: string; numeroSuivi?: string | null }) {
  return apiFetch(`/bureau/boutique/commandes/${id}`, { method: 'PUT', body: JSON.stringify(payload) });
}

/* ------------------------------ Personnalisation ------------------------------ */

/** Couleurs d'accentuation et logo, lus par le site avant son premier rendu. */
export async function fetchPersonnalisation() {
  return apiFetch('/personnalisation');
}

/** Enregistre les couleurs d'accentuation. Réservé aux administrateurs. */
export async function enregistrerPersonnalisation(payload: {
  couleurPrimaire?: string;
  couleurSecondaire?: string;
}) {
  return apiFetch('/admin/personnalisation', {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

/**
 * Remplace le logo du site.
 *
 * Pas d'en-tête Content-Type : le navigateur pose lui-même la frontière du
 * multipart, qu'on ne saurait écrire à la main.
 */
export async function televerserLogo(fichier: File) {
  const corps = new FormData();
  corps.append('logo', fichier);

  const reponse = await fetch(`${API_BASE}/admin/personnalisation/logo`, {
    method: 'POST',
    credentials: 'include',
    body: corps,
  });

  if (!reponse.ok) {
    const erreur = await reponse.json().catch(() => ({}));
    throw new Error(erreur.message || `Erreur ${reponse.status}`);
  }

  return reponse.json();
}

/** Remplace la grille des droits d'une fonction au bureau. */
export async function enregistrerDroitsRoleBureau(id: number, droits: string[]) {
  return apiFetch(`/admin/referentiels/roles-bureau/${id}/droits`, {
    method: 'PUT',
    body: JSON.stringify({ droits }),
  });
}

/* ------------------------------ Suivi des achats ------------------------------ */

export interface Achat {
  id: number;
  libelle: string;
  fournisseur: string | null;
  montant: number;
  /** Douze fûts, trois cents écocups : c'est elle qui fait le prévisionnel. */
  quantite: number;
  evenement: { id: number; nom: string } | null;
  dateAchat: string;
  modePaiement: string | null;
  statut: 'a_payer' | 'paye';
  payeLe: string | null;
  /**
   * Annulée : la ligne reste, sort des totaux, et s'affiche barrée.
   *
   * Une dépense ne s'efface pas (NF525) : l'effacement emportait le
   * justificatif du fournisseur, c'est-à-dire la pièce que réclamerait un
   * contrôle.
   */
  annule: boolean;
  annuleLe: string | null;
  annulePar: string | null;
  motifAnnulation: string | null;
  justificatifUrl: string | null;
  commentaire: string | null;
  /** Unité de la quantité : fûts, packs, kg… */
  unite: string | null;
  /** Prix unitaire quand la ligne se saisit en quantité × PU. */
  prixUnitaire: number | null;
  /** Lot de saisie : les lignes d'un même passage en caisse le partagent. */
  lot: string | null;
  /** Justificatifs du lot — plusieurs possibles, PDF ou photos. */
  documents: { id: number; url: string; nom: string }[];
}

export async function fetchAchats(): Promise<{
  success: boolean;
  annee: number;
  totaux: { depenseAnnee: number; depenseAnneePrecedente: number; resteAPayer: number };
  achats: Achat[];
}> {
  return apiFetch('/bureau/achats');
}

export async function createAchat(payload: Record<string, unknown>) {
  return apiFetch('/bureau/achats', { method: 'POST', body: JSON.stringify(payload) });
}

/** Saisit un lot d'achats : un passage en caisse, plusieurs lignes. */
export async function createAchatLot(payload: {
  dateAchat: string;
  statut: 'a_payer' | 'paye';
  evenementId: number | null;
  fournisseur: string;
  modePaiement: string;
  commentaire: string;
  lignes: { libelle: string; quantite?: number; unite?: string; prixUnitaire?: number | null; montant: number }[];
}): Promise<{ success: boolean; message: string; lot: string }> {
  return apiFetch('/bureau/achats/lot', { method: 'POST', body: JSON.stringify(payload) });
}

/** Dépose un justificatif (PDF ou photo) sur le lot — plusieurs possibles. */
export async function uploadDocumentAchatLot(lot: string, fichier: File) {
  const donnees = new FormData();
  donnees.append('document', fichier);
  const reponse = await fetch(`${API_BASE}/bureau/achats/lot/${lot}/documents`, {
    method: 'POST',
    body: donnees,
    credentials: 'include',
  });
  const json = await reponse.json().catch(() => ({}));
  if (!reponse.ok) throw new Error(json.message ?? `Erreur ${reponse.status}`);
  return json;
}

export async function updateAchat(id: number, payload: Record<string, unknown>) {
  return apiFetch(`/bureau/achats/${id}`, { method: 'PUT', body: JSON.stringify(payload) });
}

/**
 * Annule une dépense — sans l'effacer, ni son justificatif.
 *
 * L'effacement emportait la facture du fournisseur, sur le disque comme en
 * base : la pièce que réclamerait un contrôle disparaissait sans laisser de
 * trace de son passage. Annuler la laisse consultable.
 */
export async function annulerAchat(id: number, motif: string) {
  return apiFetch(`/bureau/achats/${id}/annuler`, {
    method: 'POST',
    body: JSON.stringify({ motif }),
  });
}

/**
 * Dépose le justificatif d'un achat — facture ou ticket du fournisseur.
 *
 * Pas d'en-tête Content-Type : le navigateur pose lui-même la frontière du
 * multipart.
 */
export async function uploadJustificatifAchat(id: number, fichier: File) {
  const corps = new FormData();
  corps.append('justificatif', fichier);

  const reponse = await fetch(`${API_BASE}/bureau/achats/${id}/justificatif`, {
    method: 'POST',
    credentials: 'include',
    body: corps,
  });

  if (!reponse.ok) {
    const erreur = await reponse.json().catch(() => ({}));
    throw new Error(erreur.message || `Erreur ${reponse.status}`);
  }

  return reponse.json();
}

/**
 * Enregistre un don reçu en main propre — espèces ou chèque — avec son
 * encaissement rattaché dans le même geste.
 */
export async function createBureauDon(payload: {
  montant: number;
  modePaiement: string;
  prenom?: string;
  nom?: string;
  anonyme?: boolean;
  datePaiement?: string;
}) {
  return apiFetch('/bureau/dons', { method: 'POST', body: JSON.stringify(payload) });
}

/* --------------------------------- Messagerie --------------------------------- */

/** Les boîtes de l'association que le membre connecté peut ouvrir. */
export async function fetchBoitesMail(): Promise<{
  success: boolean;
  boites: { code: string; libelle: string; adresse: string }[];
}> {
  return apiFetch('/bureau/webmail');
}

/** Les messages non lus de chaque boîte ouverte au membre (null : injoignable). */
export async function fetchNonLusBoitesMail(): Promise<{
  success: boolean;
  nonLus: Record<string, number | null>;
}> {
  return apiFetch('/bureau/webmail/non-lus');
}

/** Réglages IMAP/SMTP d'une boîte, pour configurer un téléphone. */
export interface ParametresBoite {
  association: string;
  adresse: string;
  identifiant: string;
  webmail: string;
  imap: { serveur: string; port: number; securite: string };
  smtp: { serveur: string; port: number; securite: string };
}

export async function fetchParametresBoite(code: string): Promise<{ success: boolean } & ParametresBoite> {
  return apiFetch(`/bureau/webmail/${code}/parametres`);
}

/**
 * Révèle le mot de passe d'une boîte, après re-saisie du mot de passe du
 * compte connecté. Réservé aux boîtes que la fonction autorise.
 */
export async function revelerMotDePasseBoite(
  code: string,
  motDePasse: string
): Promise<{ success: boolean; motDePasse: string }> {
  return apiFetch(`/bureau/webmail/${code}/mot-de-passe`, {
    method: 'POST',
    body: JSON.stringify({ motDePasse }),
  });
}

/* ------------------- Le transfert du courrier d une boite ----------------- */

/**
 * Les adresses vers lesquelles une boite recopie son courrier.
 *
 * Un president releve ses messages sur son adresse personnelle, comme tout le
 * monde. La boite de l association, elle, dort — et le courrier important y
 * dort avec elle, jusqu a ce qu on decouvre un mois plus tard une relance de la
 * federation.
 *
 * Le transfert ne DEPLACE rien : une copie part, le message reste. C est ce qui
 * compte le jour ou le president change — son successeur retrouve l historique.
 */
export async function fetchTransfertsBoite(
  code: string,
): Promise<{ success: boolean; boite: string; transferts: string[] }> {
  return apiFetch(`/bureau/webmail/${code}/transferts`);
}

export async function ajouterTransfertBoite(code: string, adresse: string) {
  return apiFetch(`/bureau/webmail/${code}/transferts`, {
    method: 'POST',
    body: JSON.stringify({ adresse }),
  });
}

/** Arrete la recopie. Ce qui est deja parti reste chez son destinataire. */
export async function retirerTransfertBoite(code: string, adresse: string) {
  return apiFetch(
    `/bureau/webmail/${code}/transferts?adresse=${encodeURIComponent(adresse)}`,
    { method: 'DELETE' },
  );
}

/** Ouvre une session webmail — aucune saisie de mot de passe. */
export async function ouvrirBoiteMail(code: string) {
  return apiFetch(`/bureau/webmail/${code}/session`, { method: 'POST' });
}

/* ----------------------------------- Bilan ----------------------------------- */

/** Balance d'une année : chaque poste de vente, les achats, le solde. */
export interface BilanAnnee {
  sponsors: number;
  dons: number;
  repas: number;
  concerts: number;
  totalVentes: number;
  achats: number;
  solde: number;
}

/** Balance d'une festivité, avec le détail de ses achats. */
export interface BilanEvenement {
  id: number;
  nom: string;
  dateDebut: string | null;
  ventes: { repas: number; concerts: number; sponsors?: number; total: number };
  achats: {
    total: number;
    lignes: { libelle: string; quantite: number; montant: number; statut: string }[];
  };
  solde: number;
}

export async function fetchBilan(): Promise<{
  success: boolean;
  anneeN: number;
  anneeN1: number;
  bilanN: BilanAnnee;
  bilanN1: BilanAnnee;
  /** La chronologie complète, une entrée par exercice enregistré. */
  serie: ({ annee: number } & BilanAnnee)[];
  /** Régime du sponsoring : à l'année, ou rattaché aux festivités. */
  sponsoringParEvenement: boolean;
}> {
  return apiFetch('/bureau/bilan');
}

/**
 * Balance d'une festivité et de son édition de référence.
 *
 * Sans référence explicite, le serveur devine l'édition précédente par le
 * nom : « Fête votive 2026 » se compare d'elle-même à « Fête votive 2025 ».
 */
export async function fetchBilanEvenement(
  cible: number,
  reference?: number
): Promise<{ success: boolean; cible: BilanEvenement; reference: BilanEvenement | null }> {
  const query = reference ? `&reference=${reference}` : '';
  return apiFetch(`/bureau/bilan/evenement?cible=${cible}${query}`);
}

/* ------------------------------ Coffre du bureau ------------------------------ */

export interface CompteVault {
  id: number;
  service: string;
  identifiant: string | null;
  /** L'adresse qui reçoit les liens de réinitialisation, si elle diffère de l'identifiant. */
  email: string | null;
  url: string | null;
  motDePasse: string | null;
  totp: { code: string; expireDans: number } | null;
  aTotp: boolean;
  commentaire: string | null;
  updatedAt: string;
}

/** Les comptes du coffre, secrets déchiffrés. Réservé aux administrateurs. */
export async function fetchVault(): Promise<{ success: boolean; comptes: CompteVault[] }> {
  return apiFetch('/bureau/vault');
}

/** Les codes 2FA seuls, pour le rafraîchissement toutes les 30 secondes. */
export async function fetchVaultTotp(): Promise<{
  success: boolean;
  codes: Record<string, { code: string; expireDans: number }>;
}> {
  return apiFetch('/bureau/vault/totp');
}

export async function createVaultCompte(payload: Record<string, unknown>) {
  return apiFetch('/bureau/vault', { method: 'POST', body: JSON.stringify(payload) });
}

export async function updateVaultCompte(id: number, payload: Record<string, unknown>) {
  return apiFetch(`/bureau/vault/${id}`, { method: 'PUT', body: JSON.stringify(payload) });
}

export async function deleteVaultCompte(id: number) {
  return apiFetch(`/bureau/vault/${id}`, { method: 'DELETE' });
}

/* --------------------------- Mode essai / production --------------------------- */

/**
 * Passe le site en production : purge les données financières d'essai et
 * remet la numérotation à zéro. Le mot de passe est ressaisi — une session
 * ouverte ne suffit pas à déclencher une suppression irréversible.
 */
export async function activerProduction(password: string) {
  return apiFetch('/admin/production/activer', {
    method: 'POST',
    body: JSON.stringify({ password }),
  });
}

/** Repasse le site en essai. Réservé au support de la plateforme. */
export async function desactiverProduction(password: string) {
  return apiFetch('/admin/production/desactiver', {
    method: 'POST',
    body: JSON.stringify({ password }),
  });
}

/** Supprime une fiche sponsor et tout son dossier. Réservé aux administrateurs. */
export async function deleteSponsorBureau(id: number) {
  return apiFetch(`/bureau/sponsors/${id}`, { method: 'DELETE' });
}

/* ------------------------- Partenaires institutionnels ------------------------- */

/** Mairie et communauté de communes : leur nom et leurs deux images. */
export async function fetchPartenaires() {
  return apiFetch('/admin/partenaires');
}

export async function enregistrerNomPartenaire(code: string, nom: string) {
  return apiFetch(`/admin/partenaires/${code}`, {
    method: 'PUT',
    body: JSON.stringify({ nom }),
  });
}

/**
 * Dépose le visuel ou le logo d'un partenaire.
 *
 * Pas d'en-tête Content-Type : le navigateur pose lui-même la frontière du
 * multipart, qu'on ne saurait écrire à la main.
 */
export async function televerserImagePartenaire(code: string, image: string, fichier: File) {
  const corps = new FormData();
  corps.append('fichier', fichier);

  const reponse = await fetch(`${API_BASE}/admin/partenaires/${code}/${image}`, {
    method: 'POST',
    credentials: 'include',
    body: corps,
  });

  if (!reponse.ok) {
    const erreur = await reponse.json().catch(() => ({}));
    throw new Error(erreur.message || `Erreur ${reponse.status}`);
  }

  return reponse.json();
}

export async function retirerImagePartenaire(code: string, image: string) {
  return apiFetch(`/admin/partenaires/${code}/${image}`, { method: 'DELETE' });
}

/* -------------------------------- Bot Telegram -------------------------------- */

/**
 * Demande son identité au bot, jeton enregistré à l'appui.
 *
 * Un jeton mal recopié ne se découvrirait sinon qu'au premier message à
 * envoyer, c'est-à-dire au pire moment.
 */
export async function verifierBotTelegram() {
  return apiFetch('/admin/telegram/verifier', { method: 'POST' });
}

/** Repose le logo de l'association sur le bot et sur chaque canal Telegram. */
export async function mettreAJourLogosTelegram() {
  return apiFetch('/admin/telegram/logos', { method: 'POST' });
}

/* --------------------------- Controle des visuels ------------------------------ */

/**
 * Etat d'un visuel a controler, lu depuis le lien du courriel.
 *
 * Le jeton porte par l'adresse tient lieu d'autorisation : aucune session
 * n'est requise.
 */
export async function fetchControleVisuel(jeton: string) {
  return apiFetch(`/visuels/controle/${encodeURIComponent(jeton)}`);
}

/** Applique la decision. Sans effet si le visuel a deja ete tranche ailleurs. */
export async function deciderControleVisuel(
  jeton: string,
  choix: { decision: 'valide' | 'refuse'; motif?: string }
) {
  return apiFetch(`/visuels/controle/${encodeURIComponent(jeton)}`, {
    method: 'POST',
    body: JSON.stringify(choix),
  });
}

/* ------------------------------ Réseaux sociaux ------------------------------- */

/** Réseaux renseignés et catalogue des réseaux proposés. Lecture publique. */
export async function fetchReseauxSociaux() {
  return apiFetch('/reseaux-sociaux');
}

/**
 * Remplace la liste entière des réseaux.
 *
 * L'écran envoie l'état complet plutôt qu'une suite d'ajouts et de
 * suppressions : un échec ne laisse jamais la moitié des liens écrits.
 */
export async function enregistrerReseauxSociaux(
  reseaux: { reseau: string; url: string }[]
) {
  return apiFetch('/admin/reseaux-sociaux', {
    method: 'PUT',
    body: JSON.stringify({ reseaux }),
  });
}

/* ---------------------------- Espace du sponsor ------------------------------- */

/**
 * Factures et avoirs rattachés au compte sponsor connecté.
 *
 * Chaque entrée indique si le PDF et le XML EN 16931 sont téléchargeables ;
 * les deux fichiers se récupèrent par lien direct sur l'API.
 */
export async function fetchMesFacturesSponsor() {
  return apiFetch('/sponsor/mes-factures');
}

/**
 * Devis rattachés au compte sponsor connecté.
 *
 * Un devis en attente porte son lien de validation : c'est ce que le sponsor
 * a perdu dans sa boîte, et ce pour quoi il écrirait au bureau.
 */
export async function fetchMesDevisSponsor() {
  return apiFetch('/sponsor/mes-devis');
}

/**
 * Ce que le comité sait déjà du sponsor connecté.
 *
 * Coordonnées de sa dernière souscription et derniers visuels déposés. Sert à
 * ouvrir l'écran de souscription déjà rempli, et à proposer de reprendre le
 * logo plutôt que de le redemander.
 */
export async function fetchMonDossierSponsor() {
  return apiFetch('/sponsor/mon-dossier');
}

/**
 * Corrige l'adresse du site du sponsor connecté.
 *
 * Une chaîne vide retire le site : c'est la seule façon de faire disparaître
 * un lien mort de l'annuaire et du livret.
 */
export async function enregistrerSiteWebSponsor(siteWeb: string) {
  return apiFetch('/sponsor/mon-site', {
    method: 'PUT',
    body: JSON.stringify({ siteWeb }),
  });
}

/**
 * Remplace la liste des réseaux du sponsor connecté.
 *
 * Mêmes règles et même catalogue que pour le comité : ces liens figurent sous
 * sa fiche sur la page publique des sponsors.
 */
export async function enregistrerReseauxSponsor(
  reseaux: { reseau: string; url: string }[]
) {
  return apiFetch('/sponsor/mes-reseaux', {
    method: 'PUT',
    body: JSON.stringify({ reseaux }),
  });
}

/* ------------------------------ Retrait à l'accueil --------------------------- */

/**
 * Lit une réservation depuis le jeton du QR code. Réservé au bureau.
 *
 * Le même point d'entrée sert les deux prestations : le serveur retrouve la
 * réservation, repas ou concert, et le dit dans le champ `type`. Sur le
 * terrain, on ne se demande pas ce que l'on tient avant de scanner.
 */
export async function lireRetraitRepas(token: string) {
  return apiFetch(`/retrait/${encodeURIComponent(token)}`);
}

/**
 * Enregistre une remise.
 *
 * Un nombre suffit pour un concert, où toutes les entrées se valent. Un repas
 * se remet catégorie par catégorie : un ticket adulte et un ticket enfant ne
 * donnent pas droit à la même part.
 */
export async function validerRetraitRepas(
  token: string,
  quantite: number | { nbAdultes: number; nbEnfants: number },
) {
  return apiFetch(`/retrait/${encodeURIComponent(token)}`, {
    method: 'POST',
    body: JSON.stringify(typeof quantite === 'number' ? { nombre: quantite } : quantite),
  });
}

/** Le nombre de commandes qui attendent d'être traitées : la pastille du menu. */
export async function fetchBoutiqueCommandesEnAttente(): Promise<{ success: boolean; aTraiter: number }> {
  return apiFetch('/bureau/boutique/commandes/en-attente');
}

/**
 * Interroge le répertoire des entreprises pour l'identité de l'association.
 *
 * En deux temps : un premier appel montre ce que le répertoire indique, un
 * second — `appliquer` — le reporte dans les paramètres.
 */
export async function identiteParSiret(numero: string, appliquer: boolean, ecraser: boolean) {
  return apiFetch('/admin/parametres/identite-siret', {
    method: 'POST',
    body: JSON.stringify({ numero, appliquer, ecraser }),
  });
}

/** Ce que l'export complet contiendrait : fichiers CSV et nombre de lignes. */
export async function fetchExportApercu(): Promise<{
  success: boolean;
  fichiers: { fichier: string; lignes: number }[];
  lignes: number;
}> {
  return apiFetch('/admin/export');
}
/**
 * Prépare un export : engendre son mot de passe et le met sous séquestre.
 *
 * Se demande AVANT le téléchargement, qui reste un lien ordinaire : une archive
 * de plusieurs centaines de méga-octets n'a pas à transiter par du JavaScript
 * qui la tiendrait en mémoire. Mais un lien ne rend qu'un fichier — il ne peut
 * pas afficher un mot de passe. D'où ces deux temps.
 */
export async function preparerExport(): Promise<{
  success: boolean;
  jeton: string;
  motDePasse: string;
  nomArchive: string;
  bits: number;
}> {
  return apiFetch('/admin/export/preparer', { method: 'POST' });
}


/**
 * Referme un exercice rouvert, une fois les corrections saisies.
 * Ce n'est pas la clôture annuelle : pas de verrou, pas d'année suivante.
 */
export async function refermerExercice(id: number) {
  return apiFetch(`/bureau/exercices/${id}/refermer`, { method: 'POST' });
}

/** Déverrouille (ou reverrouille) la réouverture d'un exercice clos. Exploitant seul. */
export async function autoriserReouvertureExercice(id: number, autorisee: boolean) {
  return apiFetch(`/bureau/exercices/${id}/autoriser-reouverture`, {
    method: 'POST',
    body: JSON.stringify({ autorisee }),
  });
}

/** Le plan de travail d'un exercice rouvert : échéance, chiffres, pièces. */
export async function fetchExerciceCompletion(id: number): Promise<{
  success: boolean;
  annee: number;
  jusquA: string | null;
  joursRestants: number;
  chiffres: { encaisse: number; souscrit: number; achats: number };
  factures: {
    id: number;
    numero: string;
    type: string;
    statut: string;
    sponsor: string | null;
    montant: number;
    payeeLe: string | null;
    pdfUrl: string | null;
  }[];
  achats: {
    id: number;
    libelle: string;
    fournisseur: string | null;
    montant: number;
    dateAchat: string | null;
    statut: string;
  }[];
}> {
  return apiFetch(`/bureau/exercices/${id}/completion`);
}

/** Les quatre volets du répertoire. */
export type RepertoireVolet = 'association' | 'adherents' | 'fournisseurs' | 'sponsors';

/**
 * Une ligne du répertoire.
 *
 * Un seul type pour les quatre populations : le socle — nom, ce qui situe,
 * téléphone, courriel — est commun, et chaque volet n'ajoute que ce qui lui
 * est propre. Deux formes différentes obligeraient l'écran à savoir laquelle
 * il affiche avant de savoir quoi en faire.
 */
export interface RepertoireLigne {
  id?: number | null;
  nom: string;
  fonction?: string | null;
  telephone?: string | null;
  email?: string | null;
  ville?: string | null;
  photoUrl?: string | null;
  /** Association : les compétences déclarées du bénévole. */
  competences?: string[];
  estBureau?: boolean;
  /** Adhérents : qui appeler quand il faut appeler vite. */
  urgences?: { nom: string; lien?: string | null; telephone: string }[];
  /** Fournisseurs : ce que la comptabilité sait d'eux. */
  achats?: number;
  total?: number;
  aPayer?: number;
  dernierAchat?: string | null;
  dernierLibelle?: string | null;
  /** Sponsors : leur histoire avec l'association. */
  souscriptions?: number;
  derniereSouscription?: string | null;
  siteWeb?: string | null;
}

export async function fetchRepertoire(volet: RepertoireVolet): Promise<{
  success: boolean;
  lignes: RepertoireLigne[];
  /** Vrai quand la liste est reconstituée plutôt que tenue — cas des fournisseurs. */
  derive?: boolean;
}> {
  return apiFetch(`/repertoire/${volet}`);
}

/** Une personne sur un poste, le jour de la fête. */
export interface JourJLigne {
  id: number;
  nom: string;
  telephone?: string | null;
  debut?: string | null;
  fin?: string | null;
  statutConfirmation: string;
  /** null = pas encore pointé, ce qui n'est pas « absent ». */
  presence: string | null;
  commentaire?: string | null;
}

export interface JourJPoste {
  code: string;
  libelle: string;
  lignes: JourJLigne[];
}

export async function fetchJourJ(evenementId: number, jour?: string): Promise<{
  success: boolean;
  evenement: { id: number; nom: string; lieu?: string | null };
  jour: string;
  jours: string[];
  postes: JourJPoste[];
  chiffres: { total: number; pointes: number; absents: number };
}> {
  const suffixe = jour ? `?jour=${encodeURIComponent(jour)}` : '';
  return apiFetch(`/evenements/${evenementId}/jour-j${suffixe}`);
}

/** Pointe quelqu'un. Repointer la même valeur l'efface, côté serveur. */
export async function pointerPresence(evenementId: number, creneauId: number, presence: string) {
  return apiFetch(`/evenements/${evenementId}/jour-j/${creneauId}/presence`, {
    method: 'POST',
    body: JSON.stringify({ presence }),
  });
}

/* ---------------------- Bibliothèque de l'association ---------------------- */

/** Une pièce de la vie de l'association : statuts, assurance, contrat. */
export interface DocumentAssociation {
  id: number;
  categorie: string;
  categorieLabel: string;
  titre: string;
  description?: string | null;
  dateDocument?: string | null;
  echeance?: string | null;
  /** Négatif une fois l'échéance passée, null s'il n'y en a pas. */
  joursAvantEcheance: number | null;
  nomOriginal: string;
  mimeType: string;
  taille: number;
  deposeLe?: string | null;
  deposePar?: string | null;
}

export async function fetchDocuments(): Promise<{
  success: boolean;
  categories: Record<string, string>;
  categoriesAEcheance: string[];
  tailleMax: number;
  documents: DocumentAssociation[];
}> {
  return apiFetch('/documents');
}

/** Ce qui expire bientôt ou vient d'expirer — pour le tableau de bord. */
export async function fetchEcheancesDocuments(): Promise<{
  success: boolean;
  documents: DocumentAssociation[];
}> {
  return apiFetch('/documents/echeances');
}

export async function deposerDocument(formulaire: FormData) {
  const reponse = await fetch(`${API_BASE}/documents`, {
    method: 'POST',
    credentials: 'include',
    body: formulaire,
  });
  const donnees = await reponse.json().catch(() => ({}));
  if (!reponse.ok) throw new Error(donnees.message || `Erreur ${reponse.status}`);
  return donnees;
}

export async function modifierDocument(id: number, payload: Record<string, unknown>) {
  return apiFetch(`/documents/${id}`, { method: 'PUT', body: JSON.stringify(payload) });
}

export async function supprimerDocument(id: number) {
  return apiFetch(`/documents/${id}`, { method: 'DELETE' });
}

/* ---------------------- Journal des actions sensibles ---------------------- */

export interface JournalLigne {
  id: number;
  quandLe: string | null;
  action: string;
  actionLabel: string;
  auteurNom: string | null;
  cibleType: string | null;
  cibleId: number | null;
  resume: string;
  adresseIp: string | null;
}

export async function fetchJournalActions(filtres: {
  action?: string;
  auteur?: string;
  page?: number;
}): Promise<{
  success: boolean;
  actions: Record<string, string>;
  auteurs: { id: number; nom: string }[];
  page: number;
  parPage: number;
  total: number;
  lignes: JournalLigne[];
}> {
  const params = new URLSearchParams();
  if (filtres.action) params.set('action', filtres.action);
  if (filtres.auteur) params.set('auteur', filtres.auteur);
  if (filtres.page && filtres.page > 1) params.set('page', String(filtres.page));
  const suffixe = params.toString() === '' ? '' : `?${params.toString()}`;
  return apiFetch(`/admin/journal${suffixe}`);
}

/* --------------------------- Diffusion groupée --------------------------- */

export interface DiffusionAudience {
  code: string;
  libelle: string;
  nature: string;
  /** Vrai pour la lettre d'information : elle exige un accord explicite. */
  consentement: boolean;
  destinataires: number;
}

export interface DiffusionEnvoi {
  id: number;
  envoyeLe: string | null;
  audience: string;
  audienceLabel: string;
  sujet: string;
  destinataires: number;
  echecs: number;
  auteurNom: string | null;
}

/**
 * Un modèle de message : un départ, pas un texte à envoyer tel quel.
 *
 * Ce qui est entre crochets revient à l'association — laisser des valeurs
 * plausibles garantirait qu'un message parte un jour avec la date de
 * l'exemple.
 */
export interface ModeleDiffusion {
  code: string;
  famille: string;
  titre: string;
  audience: string;
  sujet: string;
  corps: string;
}

export async function fetchDiffusions(): Promise<{
  success: boolean;
  audiences: DiffusionAudience[];
  modeles: ModeleDiffusion[];
  corpsMax: number;
  derniers: DiffusionEnvoi[];
}> {
  return apiFetch('/diffusions');
}

export async function envoyerDiffusion(payload: {
  /** Les listes retenues. Le serveur dédoublonne l'union des adresses. */
  audiences: string[];
  sujet: string;
  corps: string;
}) {
  return apiFetch('/diffusions', { method: 'POST', body: JSON.stringify(payload) });
}

/* --------------------- Diffusion segmentée : écrire à qui ------------------- */

/**
 * Un critère proposé par le serveur, et ce qu'il faut lui régler.
 *
 * Le catalogue vient du serveur et non d'une liste écrite ici : les critères
 * dépendent des modules ouverts sur l'instance, et une liste figée côté
 * navigateur proposerait « convocation à une rencontre » à une association de
 * théâtre.
 */
export interface CritereSegment {
  code: string;
  libelle: string;
  explication: string;
  famille: string;
  reglages: ReglageCritere[];
}

/**
 * Un réglage de critère : un nombre, ou un choix dans une liste.
 *
 * Les valeurs d'une liste voyagent sous « 12|Judo » — l'identifiant et le
 * libellé dans la même chaîne. C'est laid et c'est délibéré : l'écran a besoin
 * du nom pour l'afficher, la requête a besoin de l'identifiant, et les faire
 * voyager séparément obligerait à les rapprocher des deux côtés.
 */
export interface ReglageCritere {
  code: string;
  libelle: string;
  type: 'nombre' | 'liste';
  defaut?: string | number;
  valeurs?: string[];
}

/** Un champ qu'on dépose dans le sujet ou dans le corps. */
export interface VariableCourriel {
  cle: string;
  libelle: string;
  exemple: string;
  exigence?: string;
}

export interface FamilleVariables {
  famille: string;
  libelle: string;
  champs: VariableCourriel[];
}

export interface ModeleCourriel {
  code: string;
  libelle: string;
  quand: string;
  criteresSuggeres: { code: string; reglages: Record<string, unknown> }[];
  sujet: string;
  corps: string;
}

export interface SegmentEnregistre {
  id: number;
  nom: string;
  description: string | null;
  criteres: { code: string; reglages: Record<string, unknown> }[];
  modele: string | null;
  creeLe: string;
  creePar: string | null;
  employeLe: string | null;
}

export interface CritereChoisi {
  code: string;
  reglages: Record<string, unknown>;
}

/**
 * Ce que la sélection donne : un compte, et de quoi le comprendre.
 *
 * `total` et `joignables` diffèrent, et c'est l'information utile : « 84
 * correspondent, 71 ont une adresse » dit en une ligne qu'il reste treize
 * familles à joindre autrement.
 */
export interface ApercuSegment {
  total: number;
  joignables: number;
  sansAdresse: number;
  inconnus: string[];
  exemples: { id: number; nom: string; courriel: string | null }[];
}

export async function fetchSegmentation(): Promise<{
  success: boolean;
  criteres: CritereSegment[];
  variables: FamilleVariables[];
  modeles: ModeleCourriel[];
  segments: SegmentEnregistre[];
}> {
  return apiFetch('/diffusions/segmentation');
}

export async function apercuSegmentation(criteres: CritereChoisi[]): Promise<{
  success: boolean;
  apercu: ApercuSegment;
}> {
  return apiFetch('/diffusions/segmentation/apercu', {
    method: 'POST',
    body: JSON.stringify({ criteres }),
  });
}

export async function previsualiserCourriel(payload: {
  criteres: CritereChoisi[];
  sujet: string;
  corps: string;
  /** Un destinataire précis, ou le premier de la sélection. */
  adherent?: number;
}): Promise<{
  success: boolean;
  apercu: {
    destinataire: { id: number; nom: string; courriel: string };
    sujet: string;
    corps: string;
    erreur: string | null;
  } | null;
  message?: string;
}> {
  return apiFetch('/diffusions/segmentation/previsualiser', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/**
 * Dépose la campagne en file.
 *
 * Elle ne part pas dans cette requête : cinq cents courriels prennent
 * plusieurs minutes, et le serveur coupe bien avant. La réponse dit combien
 * ont été déposés, pas combien sont arrivés.
 */
export async function envoyerCampagne(payload: {
  criteres: CritereChoisi[];
  sujet: string;
  corps: string;
  modele?: string;
  segment?: number;
}): Promise<{ success: boolean; campagne?: number; deposes?: number; message: string }> {
  return apiFetch('/diffusions/segmentation/envoyer', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function enregistrerSegment(payload: {
  nom: string;
  description?: string;
  criteres: CritereChoisi[];
  modele?: string;
}) {
  return apiFetch('/diffusions/segmentation/segments', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function supprimerSegment(id: number) {
  return apiFetch(`/diffusions/segmentation/segments/${id}`, { method: 'DELETE' });
}

/* ------------- Données personnelles : conservation et effacement ------------- */

export interface DureeConservation {
  cle: string;
  libelle: string;
  aide: string;
  /** 0 désactive la purge automatique. */
  mois: number;
}

export interface PersonneTrouvee {
  type: 'adherent' | 'membre';
  id: number;
  nom: string;
  precision?: string | null;
  anonyme: boolean;
}

export async function fetchDureesConservation(): Promise<{
  success: boolean;
  durees: DureeConservation[];
}> {
  return apiFetch('/admin/donnees');
}

export async function reglerDureesConservation(durees: Record<string, number>) {
  return apiFetch('/admin/donnees', { method: 'PUT', body: JSON.stringify(durees) });
}

export async function chercherPersonnes(q: string): Promise<{
  success: boolean;
  resultats: PersonneTrouvee[];
}> {
  return apiFetch(`/admin/donnees/rechercher?q=${encodeURIComponent(q)}`);
}

export async function apercuAnonymisation(type: string, id: number): Promise<{
  success: boolean;
  apercu: { nom: string; efface: string[]; conserve: string[] };
}> {
  return apiFetch(`/admin/donnees/apercu/${type}/${id}`);
}

/**
 * Anonymise une personne. Irréversible, et protégé par le mot de passe :
 * une anonymisation qu'on peut défaire n'en est pas une.
 */
export async function anonymiser(type: string, id: number, motDePasse: string, motif: string) {
  return apiFetch(`/admin/donnees/anonymiser/${type}/${id}`, {
    method: 'POST',
    body: JSON.stringify({ motDePasse, motif }),
  });
}

/* ------------------------- Répertoire des fournisseurs ------------------------- */

export interface Fournisseur {
  id: number;
  nom: string;
  siret?: string | null;
  siren?: string | null;
  categorie: string;
  categorieLabel: string;
  adresse?: string | null;
  codePostal?: string | null;
  ville?: string | null;
  adresseComplete?: string | null;
  telephone?: string | null;
  email?: string | null;
  contact?: string | null;
  siteWeb?: string | null;
  notes?: string | null;
  actif: boolean;
  /** Ce que la comptabilité sait de lui — compté, jamais saisi. */
  achats: number;
  total: number;
  dernierAchat?: string | null;
}

export async function fetchFournisseurs(inactifs = false): Promise<{
  success: boolean;
  categories: Record<string, string>;
  fournisseurs: Fournisseur[];
  /** Noms figurant sur des achats et n'ayant pas encore de fiche. */
  aReprendre: number;
}> {
  return apiFetch(`/fournisseurs${inactifs ? '?inactifs=1' : ''}`);
}

/**
 * La recherche instantanée du serveur, appelée à la frappe.
 *
 * Sert aux écrans qui rattachent un fournisseur sans charger toute la liste —
 * la saisie d'un achat, par exemple.
 */
export async function chercherFournisseurs(q: string, categorie = ''): Promise<{
  success: boolean;
  resultats: {
    id: number;
    nom: string;
    categorie: string;
    ville: string | null;
    siret: string | null;
    telephone: string | null;
  }[];
}> {
  const params = new URLSearchParams({ q });
  if (categorie) params.set('categorie', categorie);
  return apiFetch(`/fournisseurs/recherche?${params.toString()}`);
}

export async function creerFournisseur(payload: Record<string, unknown>) {
  return apiFetch('/fournisseurs', { method: 'POST', body: JSON.stringify(payload) });
}

export async function modifierFournisseur(id: number, payload: Record<string, unknown>) {
  return apiFetch(`/fournisseurs/${id}`, { method: 'PUT', body: JSON.stringify(payload) });
}

/** Désactive plutôt que supprimer : ses achats passés le référencent. */
export async function basculerFournisseur(id: number, actif: boolean) {
  return apiFetch(`/fournisseurs/${id}/actif`, { method: 'POST', body: JSON.stringify({ actif }) });
}

/** Crée les fiches manquantes depuis les noms saisis sur les achats. */
export async function reprendreFournisseurs() {
  return apiFetch('/fournisseurs/reprendre', { method: 'POST' });
}

/* --------------------- Guides et documents explicatifs --------------------- */

export interface DocumentGuide {
  code: string;
  titre: string;
  resume: string;
  /** À qui il s'adresse, dit en clair. */
  pour: string;
  format: 'markdown' | 'html';
  octets: number;
  /** Faux quand le document existe mais ne s'adresse pas à vous. */
  ouvert: boolean;
}

export async function fetchDocumentation(): Promise<{
  success: boolean;
  documents: DocumentGuide[];
}> {
  return apiFetch('/documentation');
}

export async function lireDocument(code: string): Promise<{
  success: boolean;
  titre: string;
  format: string;
  contenu: string;
}> {
  return apiFetch(`/documentation/${code}`);
}

/**
 * L'adresse du fichier lui-même.
 *
 * Un lien plutôt qu'un appel : le navigateur télécharge avec la session en
 * cours, et un guide n'a pas à transiter par du JavaScript qui le tiendrait
 * en mémoire pour le redonner ensuite.
 */
/** L'adresse du PDF mis en page : en-tête, logo et pied de page. */
export function lienDocumentPdf(code: string): string {
  return `${API_BASE}/documentation/${code}/pdf`;
}

export function lienDocument(code: string): string {
  return `${API_BASE}/documentation/${code}/fichier`;
}

/**
 * L'état du registre : ce que la configuration remplit, ce qui manque.
 */
export async function fetchRegistreTraitements(): Promise<{
  success: boolean;
  identite: { libelle: string; valeur: string | null; ou: string }[];
  manquants: { libelle: string; ou: string }[];
}> {
  return apiFetch('/admin/donnees/registre');
}

/**
 * L'adresse du registre, dans l'un ou l'autre format.
 *
 * Le PDF se télécharge, la page s'ouvre : on ne lit pas un registre dans une
 * fenêtre de téléchargement, et on n'imprime pas depuis un onglet quand on
 * voulait un fichier.
 */
export function lienRegistre(format: 'html' | 'pdf'): string {
  return `${API_BASE}/admin/donnees/registre.${format}`;
}

/* ------------------- Clause de sous-traitance et sa signature ------------------- */

export interface SignatureClause {
  nom: string;
  qualite: string;
  email: string | null;
  signeLe: string;
  adresseIp: string | null;
  version: string;
}

export interface EtatClause {
  success: boolean;
  version: string;
  empreinte: string;
  empreinteCourte: string;
  association: SignatureClause | null;
  editeur: SignatureClause | null;
  complete: boolean;
  representantPropose: string | null;
  anterieures: {
    partie: string;
    nom: string;
    signeLe: string;
    version: string;
    empreinteCourte: string;
  }[];
  message?: string;
}

/* ------------------ Les pastilles de l'accès rapide ------------------ */

export interface CompteursAccesRapide {
  success: boolean;
  missions: number;
  documentsRecents: number;
  reunions: { sondages: number; planifiees: number };
}

export async function fetchCompteursAccesRapide(): Promise<CompteursAccesRapide> {
  return apiFetch('/acces-rapide/compteurs');
}

/* ------------------- Modèles de message de diffusion ------------------- */

export interface ModeleMessage {
  id: number;
  code: string | null;
  famille: string;
  titre: string;
  audience: string | null;
  sujet: string;
  corps: string;
  ordre: number;
  fourni: boolean;
  modifieLe: string;
}

export async function fetchModelesMessage(): Promise<{
  success: boolean;
  modeles: ModeleMessage[];
  audiences: { code: string; libelle: string }[];
  fournisManquants: number;
}> {
  return apiFetch('/modeles-message');
}

export async function creerModeleMessage(payload: Partial<ModeleMessage>) {
  return apiFetch('/modeles-message', { method: 'POST', body: JSON.stringify(payload) });
}

export async function modifierModeleMessage(id: number, payload: Partial<ModeleMessage>) {
  return apiFetch(`/modeles-message/${id}`, { method: 'PUT', body: JSON.stringify(payload) });
}

export async function supprimerModeleMessage(id: number) {
  return apiFetch(`/modeles-message/${id}`, { method: 'DELETE' });
}

export async function restaurerModelesMessage() {
  return apiFetch('/modeles-message/restaurer', { method: 'POST' });
}

export async function fetchClause(): Promise<EtatClause> {
  return apiFetch('/admin/donnees/clause');
}

/**
 * Signe la clause au nom de l'association.
 *
 * Le mot de passe accompagne la signature : c'est ce qui la rattache à une
 * personne plutôt qu'à une session restée ouverte.
 */
export async function signerClause(payload: {
  nom: string;
  qualite: string;
  lu: boolean;
  motDePasse: string;
}): Promise<EtatClause> {
  return apiFetch('/admin/donnees/clause/signer', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/* ---------------------- Explorateur des fichiers déposés ---------------------- */

export interface FichierDepose {
  nom: string;
  chemin: string;
  octets: number;
  modifie: string;
  type: string;
  /** Vrai quand aucune fiche ne le référence : il n'occupe plus que la place. */
  orphelin: boolean;
  utilisePar: string | null;
}

export interface GroupeFichiers {
  code: string;
  libelle: string;
  connu: boolean;
  nombre: number;
  octets: number;
  orphelins: number;
  fichiers: FichierDepose[];
}

/** Le procès-verbal d'une destruction : ce qui a disparu doit pouvoir se nommer. */
export interface RapportDestruction {
  nom: string;
  chemin: string;
  octets: number;
  type: string;
  modifie: string;
  empreinte: string;
  utilisePar: string | null;
  detruitLe: string;
}

export async function fetchFichiers(): Promise<{
  success: boolean;
  groupes: GroupeFichiers[];
  total: { octets: number; orphelins: number; octetsOrphelins: number };
  quota: { utilise: number; total: number; part: number; sature: boolean };
}> {
  return apiFetch('/admin/fichiers');
}

/**
 * L'adresse d'aperçu d'une pièce.
 *
 * Servie par l'application et non par un lien statique : ces fichiers sont
 * hors du web pour de bonnes raisons, et les rendre atteignables le temps
 * d'un aperçu les rendrait atteignables tout court.
 */
export function lienApercuFichier(chemin: string): string {
  return `${API_BASE}/admin/fichiers/apercu?chemin=${encodeURIComponent(chemin)}`;
}

export async function supprimerFichier(chemin: string) {
  return apiFetch('/admin/fichiers', {
    method: 'DELETE',
    body: JSON.stringify({ chemin }),
  });
}

/**
 * La gestion des droits : statuts, fonctions, personnes.
 *
 * Un seul appel pour tout l'ecran : les trois vues repondent a la meme
 * question sous trois angles, et les separer obligerait a recharger la page
 * pour voir l'effet d'un changement sur les deux autres.
 */
export async function fetchDroits() {
  return apiFetch('/admin/droits');
}

export async function updateDroitsStatut(code: string, droits: string[]) {
  return apiFetch(`/admin/droits/statuts/${encodeURIComponent(code)}`, {
    method: 'PUT',
    body: JSON.stringify({ droits }),
  });
}

export async function updateDroitsFonction(id: number, droits: string[]) {
  return apiFetch(`/admin/droits/fonctions/${id}`, {
    method: 'PUT',
    body: JSON.stringify({ droits }),
  });
}

export async function updateDroitsPersonne(id: number, payload: Record<string, unknown>) {
  return apiFetch(`/admin/droits/personnes/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

/**
 * Le coffre a son propre jeu de copies, restaurable a part.
 *
 * C'est toute sa raison d'etre : rendre un identifiant perdu sans rendre
 * avec lui la comptabilite et les inscriptions de la veille.
 */
export async function restaurerCoffre(fichier: string, motDePasse: string) {
  return apiFetch('/admin/sauvegardes/restaurer-coffre', {
    method: 'POST',
    body: JSON.stringify({ fichier, motDePasse }),
  });
}

/* ---------------------------------------------------------------------------
   Les préréglages sportifs
   ------------------------------------------------------------------------ */

/** Une discipline du catalogue, telle qu'on la propose au choix. */
/** Un mot de la discipline, et ce qu'il veut dire. */
export interface TermeDiscipline {
  terme: string;
  definition: string;
}

export interface DisciplineCatalogue {
  cle: string;
  nom: string;
  federation: string;
  /** L'adresse officielle de la fédération : la source des règles. */
  reglementation?: string | null;
  reglementationSite?: string | null;
  typePratique: string;
  saison: string;
  categories: number;
  termes: number;
  /**
   * Trois mots que cette discipline est seule à employer.
   *
   * Calculés par le serveur : un terme présent dans plusieurs disciplines ne
   * distingue rien. Les cartes affichaient jusqu'ici la même aide sur le mot
   * « licence », et la définition venait du glossaire de la discipline DÉJÀ en
   * service — un club en judo lisait la définition judo sur la carte rugby.
   */
  apercu: TermeDiscipline[];
  /** Ce que la fédération précise sur sa façon de compter les catégories. */
  note: string | null;
  competitions: string[];
  infrastructures: string[];
  mentions: string[];
}

/** Une ligne du préréglage d'un club, quelle que soit sa famille. */
export interface LignePreset {
  id: number;
  /** Faux dès que le club y a touché : c'est ce qui la protège d'une reprise. */
  fourni: boolean;
}

export interface CategoriePreset extends LignePreset {
  code: string;
  ageMin: number | null;
  ageMax: number | null;
  ageLibelle: string;
  effectif: string;
  niveau: string;
}

export interface ChampPreset extends LignePreset {
  code: string;
  libelle: string;
  type: 'texte' | 'liste' | 'date' | 'nombre';
  valeurs: string[];
  obligatoire: boolean;
}

export interface EvenementPreset extends LignePreset {
  libelle: string;
  recurrence: 'hebdomadaire' | 'annuelle' | 'ponctuelle';
  dureeMinutes: number;
}

export interface DocumentPreset extends LignePreset {
  code: string;
  libelle: string;
  sujet: string;
  corps: string;
}

export interface PresetClub {
  /**
   * L'identifiant de la discipline, requis depuis qu'il peut y en avoir
   * plusieurs : sans lui, l'ecran ne saurait pas laquelle il ferme ni a
   * laquelle il attache un numero d'affiliation.
   */
  id: number;
  /**
   * Le numero du club aupres de CETTE federation.
   *
   * Il est par discipline et non par club : le football connait l'association
   * sous un numero de district, la gymnastique sous un autre. Ce sont eux
   * qu'on porte sur les feuilles de match et les demandes de subvention.
   */
  immatriculation: string | null;
  sport: string;
  nom: string;
  federation: string;
  /** L adresse officielle de la federation : la source des regles. */
  reglementation?: string | null;
  typePratique: string;
  saison: string;
  nomenclature: { competitions?: string[]; infrastructures?: string[] };
  mentions: string[];
  appliqueLe: string;
  categories: CategoriePreset[];
  postes: Array<LignePreset & { libelle: string }>;
  encadrants: Array<LignePreset & { libelle: string }>;
  champs: ChampPreset[];
  evenements: EvenementPreset[];
  documents: DocumentPreset[];
}

/** Ce que l'application toucherait, dit avant de le faire. */
export interface ConflitsPreset {
  disciplineActuelle: string;
  changeDeDiscipline: boolean;
  personnalisees: Record<string, number>;
  total: number;
}

export async function fetchCatalogueSportif(): Promise<{
  success: boolean;
  disciplines: DisciplineCatalogue[];
}> {
  return apiFetch('/sport/catalogue');
}

/**
 * Ce que la saison en cours autorise.
 *
 * Rendu AVEC le prereglage, et non par un appel a part : l ecran doit savoir si
 * la porte est fermee avant d afficher quinze cartes cliquables. Les griser
 * apres coup, sur un refus, apprend au club qu il a essaye — pas qu il ne
 * pouvait pas.
 */
export interface EtatSaison {
  /** Faux tant que des licencies sont inscrits pour la saison courante. */
  ouvert: boolean;
  /** La phrase a afficher, avec la date a laquelle ce sera de nouveau possible. */
  motif: string | null;
}

export async function fetchPresetClub(): Promise<{
  success: boolean;
  /**
   * La premiere discipline en service.
   *
   * Gardee pour les ecrans qui n'en connaissent qu'une et n'ont rien a voir
   * avec l'omnisport. La verite est dans `presets`.
   */
  preset: PresetClub | null;
  /** Toutes les disciplines en service. Une seule, sauf module multi-disciplines. */
  presets: PresetClub[];
  /**
   * Le module est-il souscrit ?
   *
   * Dit par le serveur plutot que devine du nombre de disciplines : un club qui
   * vient de souscrire n'en a encore qu'une, et l'ecran doit deja lui proposer
   * « ajouter une discipline » plutot que « changer de discipline ». Deux
   * phrases qui ne promettent pas la meme chose.
   */
  multiDisciplines: boolean;
  saison: EtatSaison;
}> {
  return apiFetch('/sport/preset');
}

/**
 * Enregistre le numero du club aupres d'une federation.
 *
 * Une chaine vide efface le numero : c'est le geste qu'on fait quand on s'est
 * trompe de federation, et il ne demande pas de bouton separe.
 */
export async function enregistrerImmatriculation(
  presetId: number,
  immatriculation: string,
): Promise<{ success: boolean; immatriculation: string | null; message?: string }> {
  return apiFetch(`/sport/preset/${presetId}/immatriculation`, {
    method: 'PATCH',
    body: JSON.stringify({ immatriculation }),
  });
}

/**
 * Ferme une discipline : le club ne la propose plus.
 *
 * Elle n'est pas detruite. Les licences deja prises restent lisibles avec leurs
 * reglements, et la discipline se rouvre en la choisissant de nouveau — avec
 * les categories telles qu'on les avait renommees.
 */
export async function fermerDiscipline(
  presetId: number,
): Promise<{ success: boolean; message?: string; saisonEnCours?: boolean }> {
  return apiFetch(`/sport/preset/${presetId}/fermer`, { method: 'POST' });
}

/**
 * Applique une discipline au club.
 *
 * Sans `confirme`, l'appel refuse en 409 dès que le club a personnalisé quelque
 * chose, et rend le détail de ce qui serait touché. C'est voulu : on ne devine
 * pas s'il veut écraser son travail.
 */
export async function appliquerPreset(
  sport: string,
  options: { mode?: 'completer' | 'remplacer'; confirme?: boolean } = {},
) {
  return apiFetch('/sport/clubs/1/apply-preset', {
    method: 'POST',
    body: JSON.stringify({ sport, mode: options.mode ?? 'completer', confirme: options.confirme ?? false }),
  });
}

/**
 * Les cinq collections d'un préréglage, telles que l'API les nomme.
 *
 * Écrites plutôt que passées en chaîne libre : une faute de frappe dans un
 * appel donnerait un 404 que l'écran traduirait par « une erreur est survenue »,
 * et il faudrait ouvrir la console du navigateur pour savoir laquelle.
 */
export type CollectionPreset =
  | 'categories'
  | 'roles'
  | 'champs'
  | 'evenements'
  | 'documents';

/** Une ligne retirée, telle que l'écran de reprise la présente. */
export interface LigneRetiree {
  collection: CollectionPreset;
  id: number;
  libelle: string;
  retireeLe: string;
  /** Vraie si elle venait du catalogue : la remettre la rend au préréglage. */
  fournie: boolean;
}

/**
 * Ajoute une ligne au préréglage en service.
 *
 * La ligne naît « pas fournie par le catalogue » : aucune reprise du
 * préréglage ne la remplacera ni ne l'effacera.
 */
export async function ajouterLignePreset(
  collection: CollectionPreset,
  valeurs: Record<string, unknown>,
  /**
   * La discipline a laquelle la ligne appartient.
   *
   * Necessaire des qu'un club en gere plusieurs : sans elle, le serveur
   * retombe sur la premiere en service, et une categorie de gymnastique
   * partirait se poser dans le prereglage football — sans message et sans
   * trace, jusqu'a ce que les gymnastes se retrouvent ranges en U6-U7.
   */
  preset?: number,
): Promise<{ success: boolean; id?: number; message?: string }> {
  return apiFetch(`/sport/preset/${collection}`, {
    method: 'POST',
    body: JSON.stringify(preset === undefined ? valeurs : { ...valeurs, preset }),
  });
}

/**
 * Modifie une ligne.
 *
 * N'envoyez que ce qui change : les champs absents ne sont pas touchés. Toute
 * modification pose « pas fournie par le catalogue » sur la ligne, ce qui est
 * exactement ce qui protège le travail du club d'une reprise ultérieure.
 */
export async function modifierLignePreset(
  collection: CollectionPreset,
  id: number,
  valeurs: Record<string, unknown>,
): Promise<{ success: boolean; message?: string }> {
  return apiFetch(`/sport/preset/${collection}/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(valeurs),
  });
}

/**
 * Retire une ligne — sans l'effacer.
 *
 * Elle disparaît de tous les écrans, et la reprise du préréglage ne la
 * repose pas. Elle se retrouve dans {@link fetchLignesRetirees}, et se remet en
 * service par {@link remettreLignePreset} : une suppression faite de travers se
 * rattrape sans avoir à tout réappliquer.
 */
export async function retirerLignePreset(
  collection: CollectionPreset,
  id: number,
): Promise<{ success: boolean; message?: string }> {
  return apiFetch(`/sport/preset/${collection}/${id}`, { method: 'DELETE' });
}

export async function remettreLignePreset(
  collection: CollectionPreset,
  id: number,
): Promise<{ success: boolean; message?: string }> {
  return apiFetch(`/sport/preset/${collection}/${id}/remettre`, { method: 'POST' });
}

/** Un repère qu'un courrier type peut employer, avec un exemple réel. */
export interface ReperCourrier {
  /** « discipline.immatriculation » */
  repere: string;
  /** « discipline », « club », « adherent »… */
  famille: string;
  /** Ce que ce repère vaudrait aujourd'hui, ou une chaîne vide. */
  exemple: string;
}

/**
 * Les repères qu'un courrier type a le droit d'employer.
 *
 * Demandés au serveur plutôt que recopiés dans l'écran : une liste recopiée
 * divergerait le jour où l'on ajoute un repère, et le club se verrait refuser
 * un repère parfaitement valide.
 */
export async function fetchReperesCourrier(): Promise<{
  success: boolean;
  reperes: ReperCourrier[];
}> {
  return apiFetch('/sport/preset/reperes');
}

/** Un courrier type, rempli pour une licence précise. */
export interface CourrierRempli {
  code: string;
  libelle: string;
  sujet: string;
  /** Le texte, repères remplis — pour un courriel ou un copier-coller. */
  corps: string;
  /** Le même, échappé pour un affichage HTML. */
  corpsHtml: string;
}

/**
 * Les courriers types de la discipline, remplis pour cette licence.
 *
 * Ceux de SA discipline, et d'elle seule : proposer les courriers de judo pour
 * une licence de gymnastique ferait partir un accueil qui parle d'ippon à une
 * famille qui vient de s'inscrire aux agrès.
 */
export async function fetchCourriersLicence(licence: number): Promise<{
  success: boolean;
  discipline: string | null;
  courriers: CourrierRempli[];
}> {
  return apiFetch(`/bureau/licences/${licence}/courriers`);
}

/** Ce que le club a retiré d'une discipline, et qu'il peut remettre. */
export async function fetchLignesRetirees(preset?: number): Promise<{
  success: boolean;
  retirees: LigneRetiree[];
}> {
  return apiFetch(preset === undefined ? '/sport/preset/retirees' : `/sport/preset/retirees?preset=${preset}`);
}

/** Le vocabulaire de la discipline, rangé par forme normalisée. */
export async function fetchGlossaireSportif(): Promise<{
  success: boolean;
  sport: string | null;
  federation?: string;
  termes: Record<string, { terme: string; definition: string }>;
}> {
  return apiFetch('/sport/glossaire');
}

/* ----------------------- Festival : le module optionnel ---------------------- */

/**
 * Le créneau public et le contrat sont deux objets séparés côté serveur —
 * Passage et Engagement — et le restent ici : aucun type de ce bloc ne mêle
 * un horaire et un cachet. Le contrat ne circule que vers l'onglet gardé par
 * son droit à lui.
 */
export interface FestivalZone {
  id: number;
  code: string;
  libelle: string;
  type: string;
  scene: boolean;
  capacite: number | null;
  minutesPlateau: number;
  position: number;
  visiblePublic: boolean;
  notesAccessibilite: string | null;
}

export interface FestivalEdition {
  id: number;
  nom: string;
  slug: string;
  annee: number;
  dateDebut: string | null;
  dateFin: string | null;
  heureBascule: number;
  fuseau: string;
  couvreFeu: string | null;
  statut: string;
  zones: FestivalZone[];
}

export interface FestivalArtiste {
  id: number;
  nom: string;
  slug: string;
  genres: string | null;
  pays: string | null;
  bioCourte: string | null;
  bioLongue: string | null;
  siteWeb: string | null;
  lienEcoute: string | null;
  teteAffiche: boolean;
}

export interface FestivalPassage {
  id: number;
  zone: number | null;
  zoneCode: string | null;
  artiste: number | null;
  artisteNom: string | null;
  titreAffiche: string | null;
  type: string;
  statut: string;
  debut: string | null;
  fin: string | null;
  jourFestival: string | null;
  annonceLe: string | null;
  notesInternes: string | null;
}

export interface FestivalEngagement {
  id: number;
  artiste: number | null;
  artisteNom: string | null;
  cachetCentimes: number | null;
  fraisAnnexesCentimes: number | null;
  statutContrat: string;
  contratEnvoyeLe: string | null;
  contratSigneLe: string | null;
  referenceGuso: string | null;
  declareSacem: boolean;
  contactNom: string | null;
  contactEmail: string | null;
  contactTelephone: string | null;
  hebergement: string | null;
  catering: string | null;
  notes: string | null;
}

export async function fetchFestival(): Promise<{
  success: boolean;
  editions: FestivalEdition[];
  artistes: FestivalArtiste[];
  typesZone: { code: string; scene: boolean }[];
}> {
  return apiFetch('/festival');
}

export async function creerFestivalEdition(payload: Record<string, unknown>) {
  return apiFetch('/festival/editions', { method: 'POST', body: JSON.stringify(payload) });
}

export async function modifierFestivalEdition(id: number, payload: Record<string, unknown>) {
  return apiFetch(`/festival/editions/${id}`, { method: 'PUT', body: JSON.stringify(payload) });
}

export async function creerFestivalZone(edition: number, payload: Record<string, unknown>) {
  return apiFetch(`/festival/editions/${edition}/zones`, { method: 'POST', body: JSON.stringify(payload) });
}

export async function modifierFestivalZone(id: number, payload: Record<string, unknown>) {
  return apiFetch(`/festival/zones/${id}`, { method: 'PUT', body: JSON.stringify(payload) });
}

export async function supprimerFestivalZone(id: number) {
  return apiFetch(`/festival/zones/${id}`, { method: 'DELETE' });
}

export async function creerFestivalArtiste(payload: Record<string, unknown>) {
  return apiFetch('/festival/artistes', { method: 'POST', body: JSON.stringify(payload) });
}

export async function modifierFestivalArtiste(id: number, payload: Record<string, unknown>) {
  return apiFetch(`/festival/artistes/${id}`, { method: 'PUT', body: JSON.stringify(payload) });
}

export async function supprimerFestivalArtiste(id: number) {
  return apiFetch(`/festival/artistes/${id}`, { method: 'DELETE' });
}

export async function fetchFestivalProgramme(edition: number): Promise<{
  success: boolean;
  passages: FestivalPassage[];
}> {
  return apiFetch(`/festival/editions/${edition}/programme`);
}

/**
 * La pose passe par le Programmateur côté serveur : verrou, conflits,
 * journée festival. Un 409 porte le rapport — bloquants et avertissements.
 */
export async function creerFestivalPassage(edition: number, payload: Record<string, unknown>) {
  return apiFetch(`/festival/editions/${edition}/passages`, { method: 'POST', body: JSON.stringify(payload) });
}

export async function modifierFestivalPassage(id: number, payload: Record<string, unknown>) {
  return apiFetch(`/festival/passages/${id}`, { method: 'PUT', body: JSON.stringify(payload) });
}

export async function supprimerFestivalPassage(id: number) {
  return apiFetch(`/festival/passages/${id}`, { method: 'DELETE' });
}

export async function transitionFestivalPassage(
  id: number,
  transition: 'confirmer' | 'revenir' | 'annoncer' | 'annuler'
) {
  return apiFetch(`/festival/passages/${id}/${transition}`, { method: 'POST' });
}

export async function fetchFestivalEngagements(edition: number): Promise<{
  success: boolean;
  engagements: FestivalEngagement[];
}> {
  return apiFetch(`/festival/editions/${edition}/engagements`);
}

export async function poserFestivalEngagement(edition: number, payload: Record<string, unknown>) {
  return apiFetch(`/festival/editions/${edition}/engagements`, { method: 'POST', body: JSON.stringify(payload) });
}

/** Le programme public — la page du festivalier, sans session. */
export async function fetchFestivalProgrammePublic(slug: string, jour?: string): Promise<{
  success: boolean;
  edition: { nom: string; slug: string; dateDebut: string; dateFin: string; fuseau: string };
  zones: { code: string; libelle: string; position: number; notesAccessibilite: string | null }[];
  passages: {
    zone: string;
    artiste: string | null;
    artisteSlug: string | null;
    titre: string | null;
    debut: string;
    fin: string;
    jour: string;
    annule: boolean;
  }[];
}> {
  return apiFetch(`/festival/public/${slug}/programme${jour ? `?jour=${jour}` : ''}`);
}

/* ----------------------- Assurances : le module optionnel --------------------- */

export interface AssuranceContrat {
  id: number;
  type: string;
  typeLibelle: string;
  assureur: string;
  numero: string;
  debut: string | null;
  fin: string | null;
  plafondCentimes: number | null;
  primeCentimes: number | null;
  garanties: string[];
  document: number | null;
  documentTitre: string | null;
  resilieLe: string | null;
  actif: boolean;
  joursRestants: number | null;
  notes: string | null;
}

export interface AssuranceBien {
  id: number;
  type: string;
  designation: string;
  contrat: number | null;
  contratNumero: string | null;
  valeurCentimes: number | null;
  immatriculation: string | null;
  couvert: boolean;
  notes: string | null;
}

export interface AssuranceSinistre {
  id: number;
  type: string;
  typeLibelle: string;
  statut: string;
  statutLibelle: string;
  contrat: number | null;
  contratNumero: string | null;
  evenement: number | null;
  evenementNom: string | null;
  benevole: number | null;
  benevoleNom: string | null;
  survenuLe: string | null;
  lieu: string;
  description: string;
  montantEstimeCentimes: number | null;
  transmisLe: string | null;
  joursAvantTransmission: number | null;
  closLe: string | null;
  notes: string | null;
}

export async function fetchAssurance(): Promise<{
  success: boolean;
  tableauDeBord: {
    contratsActifs: number;
    echeances60Jours: number;
    sinistresOuverts: number;
    biensNonCouverts: number;
    benevolesCouverts: boolean;
  };
  contrats: AssuranceContrat[];
  biens: AssuranceBien[];
  sinistres: AssuranceSinistre[];
  evenements: { id: number; nom: string; date: string | null; lieu: string | null }[];
  documentsAssurance: { id: number; titre: string }[];
}> {
  return apiFetch('/assurance');
}

export async function creerAssuranceContrat(payload: Record<string, unknown>) {
  return apiFetch('/assurance/contrats', { method: 'POST', body: JSON.stringify(payload) });
}

export async function modifierAssuranceContrat(id: number, payload: Record<string, unknown>) {
  return apiFetch(`/assurance/contrats/${id}`, { method: 'PUT', body: JSON.stringify(payload) });
}

export async function supprimerAssuranceContrat(id: number) {
  return apiFetch(`/assurance/contrats/${id}`, { method: 'DELETE' });
}

export async function creerAssuranceBien(payload: Record<string, unknown>) {
  return apiFetch('/assurance/biens', { method: 'POST', body: JSON.stringify(payload) });
}

export async function modifierAssuranceBien(id: number, payload: Record<string, unknown>) {
  return apiFetch(`/assurance/biens/${id}`, { method: 'PUT', body: JSON.stringify(payload) });
}

export async function supprimerAssuranceBien(id: number) {
  return apiFetch(`/assurance/biens/${id}`, { method: 'DELETE' });
}

export async function declarerAssuranceSinistre(payload: Record<string, unknown>) {
  return apiFetch('/assurance/sinistres', { method: 'POST', body: JSON.stringify(payload) });
}

/** Fige la date de transmission à l'assureur — celle que les délais du contrat regardent. */
export async function transmettreAssuranceSinistre(id: number) {
  return apiFetch(`/assurance/sinistres/${id}/transmettre`, { method: 'POST' });
}

export async function changerStatutAssuranceSinistre(id: number, statut: string) {
  return apiFetch(`/assurance/sinistres/${id}/statut`, {
    method: 'POST',
    body: JSON.stringify({ statut }),
  });
}
